import { isFullyIslandedIntent } from '../intent/gridScope';
import { derivedDryCoolingForSed, NONCOINCIDENT_DRY_BULB_LIMITATION } from '../economicContribution/sedDryCoolingScreen';
import { siteResourceDetails } from '../sed/siteResourceDocumentProjection';
import { tmyHottestHoursBasisText } from '../economicContribution/tmyCoincidentWetBulb';
import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type {
  EvidenceSection,
  ParcelEvidence,
  SiteEvidenceDocument,
  OnSiteInfrastructureStatus,
} from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';
import { analysisReadinessDecisionV1Schema } from '../../types/decisionAnalysis';
import type { PathToGoDecisionEvidenceSnapshot } from '../../types/pathToGoDecision';
import { screeningPositiveDecisionAllowed } from '../pathToGo/screeningDecisionSnapshot';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { hashProjectIntent } from '../sed/shouldBootstrap';
import { presentReportsText } from '../reportRendering';
import { effectivePower, effectivePowerMwRange } from '../projectIntent';
import { readReportFindings } from '../reportFindings';
import {
  CALL_READY_COPY_GOVERNANCE,
  CALL_READY_INDUSTRY_CONTEXT,
  isCallReadyCopyApproved,
  type CallReadyCopyGovernance,
} from './callReadyCopy';
import { ECONOMIC_SOURCE_CATALOG } from '../../data/economicContribution/sourceCatalog';
import { calculateEconomicContributionScreening } from '../economicContribution/screening';
import {
  economicScreenRequestFromSite,
  resolveEconomicScreeningSiteDefaults,
} from '../economicContribution/siteScreeningInput';
import { isCurrentWaterUseScreeningResult } from '../economicContribution/waterUseScreening';
import { hashEconomicModelInput } from '../economicContribution/modelUtils';
import { staffingBasisLabel } from '../economicContribution/powerBasisDisclosure';
import type {
  EconomicContributionScreeningSnapshot,
  EconomicContributionSiteInputs,
  EconomicMetricObservation,
  EconomicProfileSnapshot,
  EconomicScreeningSiteDefaults,
  PowerMwBasis,
  WaterUseDryCoolingCapacityStatus,
  WaterUseDryCoolingDesignScreen,
} from '../../types/economicContribution';
import { renderCallReadySpokenSummary } from './callReadySpokenTemplates';
import {
  buildWorkforceProfileAnswer,
  buildWorkforceProfilePresentation,
} from './workforceProfilePresentation';
import { formatAnnualWaterBand, formatWaterRateBand } from '../waterRateFormat';
import { WET_BULB_BASIS_LABELS } from '../water/waterScreeningLabels';
import { reportCanContributeSavedResult } from '../pathToGo/reportPlan';
import { INTERCONNECTION_STATUS_LABELS } from '../reportPreferences/labels';
import { resolvePerSourceAvailability } from '../powerAvailability/resolvePerSourceAvailability';
import { resolveSiteStudyArea } from '../siteStudyArea';
import { STAGE2_SOURCES } from '../stage2/stage2Sources';
import { resolvePromotedGenerationScenario } from '../generationImpact/promotedScenario';
import { deriveIntentForSelectedAssessment } from '../generationImpact/selectedAssessmentIntent';
import type { SiteDecisionStageStatus } from '../stage2/siteDecisionStage';
import type { Stage2DecisionId } from '../stage2/stage2Types';
import { buildBriefStage, enrichStageAnswers } from './callReadyStage';
import { recommendationFirstBrief, type RecommendationFirstBriefDiagnostic } from './recommendationFirstBrief';
import {
  derivePresentedScreeningRecommendationSetV1,
  deriveScreeningRecommendationsForPresentation,
} from './screeningRecommendationsForPresentation';
import {
  buildPowerCoolingAnswers,
  framePowerCoolingAnswer,
  joinList,
  lowerFirst,
  RISK_AREA_LABEL,
  spokenCoolingStrategy,
  spokenHeatRejection,
  type PowerCoolingEvidenceRef,
  type PowerCoolingLine,
  type PowerCoolingSedSection,
} from './powerCoolingAnswers';

export type CallReadyQuestionId =
  | 'project'
  | 'workforce'
  | 'staffing-model'
  /** The lead answer: recommended DC power, cooling and BTM architecture. */
  | 'resources'
  | 'power-cooling-risks'
  | 'infrastructure'
  | 'community-concerns'
  | 'permanent-jobs'
  | 'construction-jobs'
  | 'workforce-wages'
  | 'comparables'
  | 'fiscal'
  | 'answer-basis';

export type CallReadyAnswerStatus = 'ready' | 'partial' | 'unavailable' | 'reference' | 'review-pending';
export type CallReadyAnswerBasis =
  | 'site-fact'
  | 'operator-provided'
  | 'report-finding'
  | 'public-data'
  | 'public-record'
  | 'modelled-screening'
  | 'industry-context'
  | 'availability-audit'
  | 'unavailable';

export interface CallReadyEvidenceSource {
  id: string;
  kind: 'site-record' | 'analysis' | 'model-result' | 'public-dataset' | 'public-record' | 'operator-input' | 'copy-governance';
  label: string;
  reportId?: string;
  reportType?: ReportTypeId;
  finding?: string;
  excerpt?: string;
  locator?: string;
  evidenceRole?: 'numeric-basis' | 'independent-cross-check';
  vintage?: string;
  url?: string;
}

export interface CallReadyAnswerDetail {
  label: string;
  value: string;
  group?: string;
}

/**
 * How a plotted row relates to the recorded design: the selected case leads,
 * an alternative is drawn hollow, and a benchmark or sensitivity is drawn
 * muted. Rows without an emphasis plot as a plain range.
 */
export type CallReadyVisualEmphasis = 'selected' | 'alternative' | 'benchmark';

export interface CallReadyRangeVisualRow {
  id: string;
  label: string;
  low: number;
  high: number;
  valueLabel: string;
  emphasis?: CallReadyVisualEmphasis;
}

export interface CallReadyVisualCallout {
  id: string;
  label: string;
  value: string;
  note: string;
  tone?: 'neutral' | 'caution';
}

export interface CallReadyRangeVisual {
  id: string;
  title: string;
  unitLabel: string;
  rows: readonly CallReadyRangeVisualRow[];
  /** Important non-plottable states, such as a conditional zero-water mode. */
  callouts?: readonly CallReadyVisualCallout[];
  note?: string;
  /**
   * `context` is secondary comparison context that should not visually lead
   * the answer. `screened-out` is a case the site record rules out (wet
   * cooling in a High water-stress basin): kept for the record, collapsed,
   * and explained by `reason`.
   */
  presentation?: 'primary' | 'context' | 'screened-out';
  /** Why a `screened-out` visual does not apply to this site. */
  reason?: string;
}

/**
 * What the reader should do with an answer, stated only from the answer's own
 * status and the site facts already on the record. It adds no figures and is
 * not evidence; the brief exporters carry it for internal screening only.
 */
export interface CallReadyRecommendation {
  text: string;
  /** `proceed`: present as is; `caution`: present with the stated caveat; `input`: resolve the gap first. */
  tone: 'proceed' | 'caution' | 'input';
}

/**
 * The two decision stages (`DECISION_STAGE_LABEL`): Stage 1 · Early screening
 * and Stage 2 · Mature validation. An answer line's `openIn` says in which
 * stage an open line closes.
 */
export type CallReadyBriefStage = 'early-screening' | 'mature-validation';

export interface CallReadyStageContext {
  briefStage: CallReadyBriefStage;
  screeningComplete: boolean;
  trigger: 'promoted-scenario' | 'grid-only' | null;
  promotedScenario: { id: string; name: string } | null;
  label: string;
  contextLine: string;
  basis: readonly { kind: 'scenario' | 'record'; label: string; value?: string }[];
}
export interface CallReadyConfirmItem { label: string; note?: string; route?: CallReadyRoute }
export interface CallReadyLeadFact { label: string; value: string; note?: string; state: 'known' | 'not-recorded' }
export interface CallReadyCapacityPayload {
  normalNetMW: number; nMinusOneNetMW: number; criticalLoadMW: number;
  reserveRequiredMW: number | null;
  verdict: 'covers' | 'reserve-short' | 'n1-short' | 'not-determined';
}
export interface CallReadyFootprintPayload {
  totalAcres: number;
  parts: ReadonlyArray<{ id: 'generation' | 'cooling' | 'switchyard' | 'ccs' | 'compression' | 'bess'; label: string; acres: number }>;
}
export interface CallReadyValidationBlock {
  sourceLabel: string; caption?: string; details: readonly CallReadyAnswerDetail[];
  capacity?: CallReadyCapacityPayload;
  footprint?: CallReadyFootprintPayload;
  receptors?: ReadonlyArray<{ id: string; exceeds: boolean; verdict?: 'pass' | 'fail' | 'indeterminate' }>;
}


/**
 * One labelled line of a line-led answer (the power & cooling lead): the value
 * and where it comes from — recorded on the site, recommended by the platform,
 * or not yet determined with the step or decision that settles it.
 */
export interface CallReadyAnswerLine {
  id: string;
  label: string;
  value: string;
  basis: 'recorded' | 'recommended' | 'not-determined';
  /** A few words of basis. */
  basisNote: string;
  /**
   * Where a line that is not (fully) determined closes: `early-screening` is a
   * Stage 1 gap with a `nextStep`; `mature-validation` a Stage 2 decision field.
   */
  openIn?: CallReadyBriefStage;
  nextStep?: string;
  route?: CallReadyRoute;
  spokenLabel?: string;
  settlesWith?: string;
  /** The platform's full reasoning, when longer than the basis note. */
  rationale?: string;
  /** Retained site inputs beside the live recommendation-first view. */
  recordedInput?: { value: string; basisNote: string; nextStep?: string };
}

/** An evidenced risk with one recommended action, or a topic not yet assessed. */
export interface CallReadyRiskItem {
  id: string;
  /** Short area label: On-site generation, Power supply, Cooling and water or Plant siting. */
  area: string;
  title: string;
  state: 'evidenced' | 'not-assessed';
  severity: 'significant' | 'noted';
  finding: string;
  source: string;
  action: string;
  origin?: CallReadyBriefStage;
  route?: CallReadyRoute;
  spokenResponse?: string;
  where?: string;
}

export interface CallReadyAnswer {
  id: CallReadyQuestionId;
  ordinal: number;
  appliesFrom?: CallReadyBriefStage;
  expandsWith?: string;
  validation?: CallReadyValidationBlock;
  lineBalance?: string;
  leadFacts?: readonly CallReadyLeadFact[];
  confirm?: readonly CallReadyConfirmItem[];
  confirmLabel?: 'To confirm' | 'To resolve';
  clearTopics?: readonly string[];
  shortLabel: string;
  question: string;
  status: CallReadyAnswerStatus;
  bases: readonly CallReadyAnswerBasis[];
  summary: string;
  /**
   * The supporting datasheet of the power and cooling answer; the spoken
   * summary remains a separate, copyable paragraph.
   */
  lines?: readonly CallReadyAnswerLine[];
  /** Evidenced risks with actions; unassessed topics say "not yet assessed". */
  risks?: readonly CallReadyRiskItem[];
  details: readonly CallReadyAnswerDetail[];
  evidence: readonly CallReadyEvidenceSource[];
  /** Compact, presentation-safe ranges for the stakeholder brief. */
  visuals?: readonly CallReadyRangeVisual[];
  /**
   * When set, the visuals are supporting quantities, not the answer: they
   * render collapsed under this label.
   */
  supportingVisualsLabel?: string;
  nextInput?: string;
  /**
   * The tab that closes `nextInput`, when the answer knows it; otherwise the
   * brief routes the question by its default phase.
   */
  route?: CallReadyRoute;
  /** Per-question guidance for the reader; see {@link CallReadyRecommendation}. */
  recommendation?: CallReadyRecommendation;
  unavailableCause?: string;
  /** Populated only by an approved, versioned communications template. */
  spokenSummary?: string;
  /**
   * What a report prompt's call-ready framing states for this question, when
   * it differs from the on-screen answer (`renderCallReadyFramingForPrompt`
   * reads `promptFraming ?? answer`). Figure-free like the rest of the framing.
   */
  promptFraming?: CallReadyPromptFraming;
}

/**
 * Where an answer's next step is done: a work phase (`build` → Analyses,
 * `verify` → the first verification item).
 */
export type CallReadyRoute =
  | { kind: 'phase'; phase: 'build' | 'verify' }
  | { kind: 'intent-field'; field: string }
  | { kind: 'stage2-decision'; id: Stage2DecisionId }
  | { kind: 'generation-workbench' };

/** The framing a report prompt receives for one question (status and basis, never figures). */
export interface CallReadyPromptFraming {
  shortLabel: string;
  status: CallReadyAnswerStatus;
  bases: readonly CallReadyAnswerBasis[];
  nextInput?: string;
}

export interface CallReadyFindingsModel {
  stage?: CallReadyStageContext;
  siteId: string;
  siteName: string;
  generatedFrom: 'existing-site-data-and-findings';
  copyGovernance: CallReadyCopyGovernance;
  copyApproved: boolean;
  recordState: 'loading' | 'settled';
  questions: readonly CallReadyAnswer[];
  /** Frozen pre-display question framing, including fiscal when hidden from an early brief. */
  promptQuestions?: readonly CallReadyAnswer[];
  /** Ready SITE answers only; reference guidance never inflates coverage. */
  readyCount: number;
  siteQuestionCount: number;
  referenceCount: number;
  evidenceSourceCount: number;
  economicContributionEnabled: boolean;
  economicSnapshot: EconomicContributionScreeningSnapshot | null;
  /** Public workforce profile can hydrate independently of the MW/jobs screening snapshot. */
  economicProfile?: EconomicProfileSnapshot | null;
  economicSnapshotAuthority: 'server-authored' | 'local-unavailable-preview' | null;
  economicInputs: EconomicContributionSiteInputs;
  /** Intent-first, source-aware defaults for the screening controls. */
  economicScreeningDefaults: EconomicScreeningSiteDefaults;
  economicDefaultLoadMw: number | null;
  economicDefaultLoadRange: { low: number; high: number } | null;
  economicDefaultLoadBasis: PowerMwBasis;
}

/** Explicit live screening basis; frozen historical decision evidence is never
 * substituted for this host-rebuilt context. Raw bytes are checked BEFORE any
 * presentation scrubbing so a historical hash cannot attest changed text. */
export interface CurrentScreeningRecommendationBasis {
  evidence: PathToGoDecisionEvidenceSnapshot | null;
  report: SiteRagReport | null;
}

export interface BuildCallReadyFindingsOptions {
  decisionStage?: Pick<SiteDecisionStageStatus, 'stage1Complete' | 'gridOnly'> | null;
  economicContributionEnabled?: boolean;
  /** Undefined preserves non-Brief callers. Explicit null/absence withholds
   * current screening advice instead of picking an unrelated latest report. */
  screeningRecommendationBasis?: CurrentScreeningRecommendationBasis | null;
  /** UI-only recommendation perspective; generation callers keep their existing input framing. */
  recommendationFirst?: boolean;
  analysesState?: 'loading' | 'settled';
  /** Live SED candidate. It is ignored unless its id and version exactly match the site pointer. */
  sed?: SiteEvidenceDocument | null;
  /** Today as an ISO date for the equipment lead-time rules; defaults to the current date. */
  asOf?: string;
}

const REFERENCE_QUESTION_IDS: ReadonlySet<CallReadyQuestionId> = new Set([
  'workforce',
  'staffing-model',
]);

interface ReportEvidence {
  report: SiteRagReport;
  source: CallReadyEvidenceSource;
}

function reportTypeOf(report: SiteRagReport): ReportTypeId {
  return report.reportType ?? 'feasibility';
}

function reportRecency(report: SiteRagReport): number {
  const timestamp = Date.parse(report.updatedAt ?? report.createdAt);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

/**
 * Decide whether a report may supply spoken/stakeholder-brief evidence.
 * Currency is disclosed separately: a successfully saved, readable revision
 * remains usable during a stale/queued/failed refresh, while incomplete,
 * unauthorized, corrupt, draft, or withheld bodies remain fail-closed.
 */
function reportCanSupplyBriefEvidence(report: SiteRagReport, site: SiteEntity): boolean {
  // A shared display name cannot override an explicit modern site identity.
  if (report.siteId != null && report.siteId !== site.id) return false;
  if (!reportCanContributeSavedResult(report, reportTypeOf(report))) return false;

  const rawReadiness = report.analysisReadiness ?? report.analysisRevision?.readiness;
  if (rawReadiness != null) {
    const parsed = analysisReadinessDecisionV1Schema.safeParse(rawReadiness);
    if (!parsed.success) return false;
    const readiness = parsed.data;

    // A queued/refreshing overlay may still carry the last passing, viewable
    // body. A held/failed attempt is deliberately excluded from spoken copy,
    // even though its saved result can remain a discounted calculation input.
    return (
      readiness.canView
      && readiness.identityValid
      && readiness.qualityReady
      && readiness.narrativeReady
      && (
        (readiness.ready && readiness.canUseForDecision)
        || readiness.state === 'queued'
        || readiness.state === 'refreshing'
      )
    );
  }

  // Legacy audit holds remain visible in the analysis workspace and can feed
  // governed calculations, but must not be promoted into call-ready prose.
  return (
    report.qualityHold !== 'needs-revision'
    && (report.qualityHoldFindings?.length ?? 0) === 0
  );
}

/** Resolve ONLY the exact live Stage 1 source supplied by the host. This is
 * read-only display qualification; it never opens a decision gate or rewrites
 * the record, report, source receipt or Intent. */
export function resolveCurrentScreeningRecommendationReport(
  site: SiteEntity,
  basis: CurrentScreeningRecommendationBasis | null | undefined,
): SiteRagReport | null {
  const evidence = basis?.evidence;
  const report = basis?.report;
  const snapshot = evidence?.screening;
  if (!evidence || !report || !snapshot?.report || !screeningPositiveDecisionAllowed(evidence)) return null;
  if (site.sedStatus === 'bootstrapping' || site.sedStatus === 'failed'
    || report.siteId !== site.id || evidence.sedId !== site.sedId || evidence.sedVersion !== site.sedVersion
    || evidence.intentHash !== hashProjectIntent(site.projectIntent ?? null)
    || (report.projectIntentAtGen?.screeningMode ?? undefined) !== (site.projectIntent?.screeningMode ?? undefined)
    || (evidence.projectIntent?.screeningMode ?? undefined) !== (site.projectIntent?.screeningMode ?? undefined)
    || !site.studyAreaScope || !report.studyAreaScope || !snapshot.studyAreaScope
    || canonicalJson(snapshot.studyAreaScope) !== canonicalJson(site.studyAreaScope)
    || canonicalJson(report.studyAreaScope) !== canonicalJson(site.studyAreaScope)
    || report.id !== snapshot.report.id || snapshot.report.reportType !== 'feasibility'
    || snapshot.report.version !== (report.version ?? 1) || snapshot.report.minorVersion !== (report.minorVersion ?? 0)
    || snapshot.report.createdAt !== report.createdAt
    || snapshot.sourceHash !== report.reportRevision?.sourceHash
    || snapshot.reportContentHash !== hashCanonicalJson(report)
    || !reportCanSupplyBriefEvidence(report, site)) return null;
  return report;
}

/**
 * Discriminated diagnostic explaining why `resolveCurrentScreeningRecommendationReport`
 * succeeded or withheld a report, so Brief never collapses missing, stale-SED,
 * stale-mode, quality-hold, or unbound states into an identical generic message.
 */
export function diagnoseCurrentScreeningRecommendationBasis(
  site: SiteEntity,
  basis: CurrentScreeningRecommendationBasis | null | undefined,
  fallbackReport?: SiteRagReport | null,
): RecommendationFirstBriefDiagnostic {
  if (resolveCurrentScreeningRecommendationReport(site, basis) != null) {
    return {
      state: 'ready',
      reason: 'Current saved screening recommendation is bound and verified.',
      nextStep: 'Review the saved screening recommendations before adopting into Project Intent.',
    };
  }
  if (site.sedStatus === 'bootstrapping') {
    return {
      state: 'sed-loading',
      reason: 'No current saved screening recommendation is available for this question because site evidence (SED) is still collecting.',
      nextStep: 'Wait for site evidence collection to finish, then review or refresh Screening Assessment.',
    };
  }
  if (site.sedStatus === 'failed') {
    return {
      state: 'sed-failed',
      reason: 'No current saved screening recommendation is available for this question because site evidence (SED) collection failed.',
      nextStep: 'Retry site evidence collection before running Screening Assessment.',
    };
  }
  const candidate = basis?.report ?? fallbackReport ?? null;
  const evidence = basis?.evidence ?? null;
  if (!candidate) {
    return {
      state: 'missing-report',
      reason: 'No current saved screening recommendation is available for this question because no completed Screening Assessment is saved for this site.',
      nextStep: 'Run Screening Assessment to generate power, cooling, and sizing recommendations. Recorded inputs remain comparison constraints.',
    };
  }
  if (candidate.siteId != null && candidate.siteId !== site.id) {
    return {
      state: 'identity-mismatch',
      reason: 'No current saved screening recommendation is available for this question because the candidate report belongs to a different site identity.',
      nextStep: 'Reload the current site workspace and review Screening Assessment.',
    };
  }
  if (candidate.qualityHold === 'needs-revision' || (candidate.qualityHoldFindings?.length ?? 0) > 0) {
    return {
      state: 'quality-hold',
      reason: 'No current saved screening recommendation is available for this question because the saved Screening Assessment is under a quality/narrative hold.',
      nextStep: 'Review the quality hold or regenerate Screening Assessment. Recorded inputs remain comparison constraints.',
    };
  }
  const savedMode = candidate.projectIntentAtGen?.screeningMode ?? evidence?.projectIntent?.screeningMode;
  const liveMode = site.projectIntent?.screeningMode;
  if (savedMode !== liveMode) {
    return {
      state: 'stale-intent-mode',
      reason: `No current saved screening recommendation is available for this question because the saved Screening Assessment was generated in ${savedMode ?? 'evaluate-proposal'} mode rather than ${liveMode ?? 'recommend'} mode.`,
      nextStep: 'Regenerate Screening Assessment in Recommend-an-Approach mode to produce current recommendations. Recorded inputs remain comparison constraints.',
    };
  }
  const savedSedId = evidence?.sedId ?? candidate.sedId;
  const savedSedVer = evidence?.sedVersion ?? candidate.sedVersionUsed;
  if ((site.sedId && savedSedId && savedSedId !== site.sedId) || (site.sedVersion != null && savedSedVer != null && savedSedVer !== site.sedVersion)) {
    return {
      state: 'stale-sed',
      reason: `No current saved screening recommendation is available for this question because site evidence changed after the saved Screening Assessment (saved SED v${savedSedVer ?? '?'} vs live SED v${site.sedVersion ?? '?'}).`,
      nextStep: 'Refresh Screening Assessment against the current site evidence. Recorded inputs remain comparison constraints.',
    };
  }
  if (evidence && evidence.intentHash !== hashProjectIntent(site.projectIntent ?? null)) {
    return {
      state: 'stale-intent',
      reason: 'No current saved screening recommendation is available for this question because Project Intent inputs changed after the saved Screening Assessment was generated.',
      nextStep: 'Refresh Screening Assessment to evaluate the updated Project Intent. Recorded inputs remain comparison constraints.',
    };
  }
  const snapshot = evidence?.screening;
  if (
    !site.studyAreaScope
    || !candidate.studyAreaScope
    || (snapshot?.studyAreaScope && canonicalJson(snapshot.studyAreaScope) !== canonicalJson(site.studyAreaScope))
    || canonicalJson(candidate.studyAreaScope) !== canonicalJson(site.studyAreaScope)
  ) {
    return {
      state: 'stale-study-area',
      reason: 'No current saved screening recommendation is available for this question because the site study-area boundary changed or is unconfirmed relative to the saved Screening Assessment.',
      nextStep: 'Confirm the study-area boundary and refresh Screening Assessment. Recorded inputs remain comparison constraints.',
    };
  }
  if (
    snapshot?.report
    && (snapshot.sourceHash !== candidate.reportRevision?.sourceHash
      || snapshot.reportContentHash !== hashCanonicalJson(candidate))
  ) {
    return {
      state: 'changed-body',
      reason: 'No current saved screening recommendation is available for this question because the report body or revision hash differs from the verified decision snapshot.',
      nextStep: 'Reload or refresh Screening Assessment so the decision snapshot matches the immutable report revision.',
    };
  }
  return {
    state: 'unbound-basis',
    reason: 'No current saved screening recommendation is available for this question because the saved Screening Assessment is not bound to a current live decision snapshot.',
    nextStep: 'Review or refresh Screening Assessment. Recorded inputs remain comparison constraints, not a recommended approach.',
  };
}

function latestReportsByType(
  analyses: readonly SiteRagReport[],
  site: SiteEntity,
): ReadonlyMap<ReportTypeId, SiteRagReport> {
  const latestUsable = new Map<ReportTypeId, SiteRagReport>();
  const latestAny = new Map<ReportTypeId, SiteRagReport>();

  for (const report of analyses) {
    const type = reportTypeOf(report);
    const usable = reportCanSupplyBriefEvidence(report, site);

    if (usable) {
      const curUsable = latestUsable.get(type);
      if (!curUsable || reportRecency(report) > reportRecency(curUsable)) {
        latestUsable.set(type, report);
      }
    }

    const curAny = latestAny.get(type);
    if (!curAny || reportRecency(report) > reportRecency(curAny)) {
      latestAny.set(type, report);
    }
  }

  const finalMap = new Map<ReportTypeId, SiteRagReport>(latestUsable);
  for (const [type, report] of latestAny) {
    if (!finalMap.has(type)) {
      finalMap.set(type, report);
    }
  }

  return finalMap;
}

function evidenceForTypes(
  reports: ReadonlyMap<ReportTypeId, SiteRagReport>,
  types: readonly ReportTypeId[],
  site: SiteEntity,
): ReportEvidence[] {
  return types.flatMap((type) => {
    const report = reports.get(type);
    if (!report) return [];

    const usable = reportCanSupplyBriefEvidence(report, site);
    const isFailed = report._metadataOnly === true || report.degradedMode === true;
    const isStale = report.requiresRegeneration != null;
    const isHeld = report.qualityHold === 'needs-revision' || (report.qualityHoldFindings?.length ?? 0) > 0;
    const isDraft = report.generationTier === 'draft' || report.isDraft === true;

    let unavailableReason: string | undefined = undefined;
    if (!usable) {
      if (isFailed) {
        unavailableReason = 'generation fault';
      } else if (isStale) {
        unavailableReason = 'stale evidence';
      } else if (isHeld) {
        unavailableReason = 'held by narrative reviewer';
      } else if (isDraft) {
        unavailableReason = 'draft preview';
      } else {
        unavailableReason = 'unusable analysis';
      }
    }

    const [finding] = readReportFindings(report, 1, { truncateAt: 'none' }).items;

    return [{
      report,
      source: {
        id: `analysis:${report.id}`,
        kind: 'analysis' as const,
        label: REPORT_TYPE_REGISTRY[type].shortLabel,
        reportId: report.id,
        reportType: type,
        finding: unavailableReason
          ? `Analysis unavailable: ${unavailableReason}.`
          : finding ?? 'No finding available.',
        vintage: report.updatedAt ?? report.createdAt,
        ...(unavailableReason ? { unavailableReason } : {}),
      },
    }];
  });
}

function sourceDetails(evidence: readonly ReportEvidence[]): CallReadyAnswerDetail[] {
  return evidence.map(({ source }) => ({
    label: source.label,
    value: source.finding ?? 'No finding available.',
  }));
}

function formatWhole(value: number, unit: string): string {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)} ${unit}`;
}

/** The shared band formatter; this file's local copy was the correct one and is
 * now the single implementation. See src/lib/waterRateFormat.ts. */
const formatWaterRate = formatWaterRateBand;

/** The shared annual band formatter (same unit rule, unit printed once). */
const formatAnnualWater = formatAnnualWaterBand;

function annualEnergyMwh(
  loadMw: { low: number; high: number },
  utilization: { low: number; high: number },
): { low: number; high: number } {
  const hoursPerYear = 8_760;
  return {
    low: loadMw.low * utilization.low * hoursPerYear,
    high: loadMw.high * utilization.high * hoursPerYear,
  };
}

function formatAnnualEnergy(range: { low: number; high: number }): string {
  const scale = range.high >= 1_000_000
    ? { divisor: 1_000_000, unit: 'TWh/year' }
    : range.high >= 1_000
      ? { divisor: 1_000, unit: 'GWh/year' }
      : { divisor: 1, unit: 'MWh/year' };
  const format = (value: number): string => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: scale.divisor === 1 ? 0 : 2,
    maximumFractionDigits: scale.divisor === 1 ? 0 : 2,
  }).format(value / scale.divisor);
  return Math.round(range.low) === Math.round(range.high)
    ? `${format(range.low)} ${scale.unit}`
    : `${format(range.low)}–${format(range.high)} ${scale.unit}`;
}

function formatHoursRange(range: { low: number; high: number }): string {
  const format = (value: number): string => new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(value);
  return Math.round(range.low) === Math.round(range.high)
    ? `${format(range.low)} hours/year`
    : `${format(range.low)}–${format(range.high)} hours/year`;
}

function formatMwBand(range: { low: number; high: number }): string {
  const low = formatDecimal(range.low, 2);
  const high = formatDecimal(range.high, 2);
  return Math.abs(range.low - range.high) < 0.0001
    ? `${low} MW`
    : `${low}–${high} MW`;
}

function dryCoolingCapacityStatusLabel(status: WaterUseDryCoolingCapacityStatus): string {
  switch (status) {
    case 'dry-only-screening-candidate':
      return 'candidate within the published approach range';
    case 'vendor-selection-required':
      return 'conditional; vendor selection or oversizing required';
    case 'assist-or-trim-required':
      return 'adiabatic assist, trim cooling, or vendor exception indicated';
    case 'reference-temperature-exceeded':
      return 'outside the compressorless dry-only temperature envelope';
  }
}

function dryCoolingClassSummary(
  screen: WaterUseDryCoolingDesignScreen,
  mode: 'dry' | 'adiabatic',
): string {
  return screen.classChecks
    .map((check) => `${check.waterClass} ${dryCoolingCapacityStatusLabel(
      mode === 'dry' ? check.dryStatus : check.adiabaticStatus,
    )}`)
    .join(' · ');
}

function rangeVisualRow(
  id: string,
  label: string,
  range: { low: number; high: number } | null | undefined,
  valueLabel: string,
): CallReadyRangeVisualRow | null {
  if (range == null || !Number.isFinite(range.low) || !Number.isFinite(range.high)) return null;
  return { id, label, low: range.low, high: range.high, valueLabel };
}

function statusFromSignals(present: number, expected: number): CallReadyAnswerStatus {
  if (present <= 0) return 'unavailable';
  return present >= expected ? 'ready' : 'partial';
}

function recommend(tone: CallReadyRecommendation['tone'], text: string): CallReadyRecommendation {
  return { tone, text };
}

function uniqueBases(bases: readonly CallReadyAnswerBasis[]): CallReadyAnswerBasis[] {
  return [...new Set(bases)];
}

type TrustedSedSection<T> = EvidenceSection<T> & { data: T };

const REPORT_DERIVED_SED_PROVENANCE = new Set<NonNullable<EvidenceSection<unknown>['provenance']>>([
  'report-derived',
  'report+provider',
  'report-overrides-provider',
]);

/**
 * Restrict call-ready facts to the exact SED the site currently points at.
 * A partially collected SED may still contribute independently trustworthy
 * sections; failed or in-flight documents may not.
 */
function exactCurrentSed(
  site: SiteEntity,
  candidate: SiteEvidenceDocument | null | undefined,
): SiteEvidenceDocument | null {
  if (!candidate || !site.sedId || site.sedVersion == null) return null;
  if (site.sedStatus === 'bootstrapping' || site.sedStatus === 'failed') return null;
  return candidate.id === site.sedId && candidate.version === site.sedVersion
    ? candidate
    : null;
}

/**
 * SED values used here must be independently grounded and internally clean.
 * Explicit anomalies, carry-forward values, inference-only facts, and every
 * report feedback path are excluded so a report cannot cite itself through
 * the SED.
 */
function isTrustedSedSection(
  section: (Omit<EvidenceSection<unknown>, 'status'> & { status: OnSiteInfrastructureStatus }) | null | undefined,
): boolean {
  if (!section || section.data == null) return false;
  if (section.status === 'unavailable' || section.status === 'anomalous' || section.status === 'verified-from-report') return false;
  if ((section.anomalies?.length ?? 0) > 0 || section.carriedForward != null) return false;
  if (section.sourceGrade === 'inference') return false;
  if (section.qualification === 'unknown' || section.qualification === 'unavailable') return false;
  if (section.provenance && REPORT_DERIVED_SED_PROVENANCE.has(section.provenance)) return false;
  if (section.provenanceDetail?.derivedFromReport != null) return false;
  return true;
}

function trustedSedSection<T>(
  section: EvidenceSection<T> | null | undefined,
): TrustedSedSection<T> | null {
  return isTrustedSedSection(section) ? section as TrustedSedSection<T> : null;
}

function trustedSedSectionDetail<T>(
  section: EvidenceSection<T> | null | undefined,
): { status: 'trusted'; section: TrustedSedSection<T> } | { status: 'un-run'; message: string } | { status: 'failed'; reason: string } {
  if (!section || section.data == null) {
    return { status: 'un-run', message: 'No public source evidence was collected.' };
  }
  if (section.status === 'unavailable') {
    return { status: 'failed', reason: section.emptyReason ?? 'The provider query failed.' };
  }
  const trusted = trustedSedSection(section);
  if (trusted) {
    return { status: 'trusted', section: trusted };
  }
  return { status: 'failed', reason: section.emptyReason ?? 'The public source evidence is untrusted or invalid.' };
}

function trustedParcelLandUseSection(
  section: EvidenceSection<ParcelEvidence> | null | undefined,
): { section: TrustedSedSection<ParcelEvidence>; acreageTrusted: boolean } | null {
  const fullyTrusted = trustedSedSection(section);
  if (fullyTrusted) return { section: fullyTrusted, acreageTrusted: true };
  if (!section?.data) return null;
  const anomalies = section.anomalies ?? [];
  const onlyRegistryAcreageAnomaly = anomalies.length > 0 && anomalies.every(anomaly => (
    /^Parcel record unavailable from registry; using boundary-derived acreage only\.?$/i.test(anomaly.trim())
  ));
  if (!onlyRegistryAcreageAnomaly) return null;
  const otherwiseTrusted = trustedSedSection({ ...section, anomalies: [] });
  if (!otherwiseTrusted) return null;
  return { section: otherwiseTrusted, acreageTrusted: false };
}

/**
 * Read the workforce payload independently from the multi-topic economic
 * envelope. The envelope can carry a scoped comparable-research anomaly (for
 * example one official project page being unavailable) while its frozen
 * federal workforce snapshot remains complete and source-addressable. Treating
 * that unrelated anomaly as a workforce failure made a valid county profile
 * disappear from the Stakeholder Brief.
 */
function trustedSedWorkforceProfile(
  sed: SiteEvidenceDocument | null,
): EconomicProfileSnapshot | null {
  const section = sed?.economicEvidence;
  if (!section || section.data == null) return null;
  if (section.status === 'unavailable' || section.status === 'anomalous' || section.status === 'verified-from-report') return null;
  if (section.carriedForward != null || section.sourceGrade === 'inference') return null;
  if (section.qualification === 'unknown' || section.qualification === 'unavailable') return null;
  if (section.provenance && REPORT_DERIVED_SED_PROVENANCE.has(section.provenance)) return null;
  if (section.provenanceDetail?.derivedFromReport != null) return null;
  if ((section.anomalies ?? []).some((anomaly) => (
    /\b(?:workforce|economic profile|OEWS|LAUS|BLS|occupation(?:al)? wage)\b/i.test(anomaly)
  ))) return null;

  const profile = section.data.workforceProfile;
  const screeningLocation = section.data.screening?.location;
  if (
    !profile
    || profile.schemaVersion !== 1
    || !profile.snapshotId.trim()
    || !/^\d{5}$/.test(profile.countyFips)
    || !/^\d{2}$/.test(profile.stateFips)
    || !/^[A-Z]{2}$/.test(profile.stateCode)
    || !profile.countyName.trim()
    || !Number.isFinite(Date.parse(profile.generatedAt))
    || !Array.isArray(profile.observations)
    || (screeningLocation != null && (
      screeningLocation.countyFips !== profile.countyFips
      || screeningLocation.stateFips !== profile.stateFips
    ))
  ) return null;
  return profile;
}

function cleanFactText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || /^(?:unknown|unavailable|not available|n\/?a|none)$/i.test(trimmed)) return null;
  return trimmed.length <= 160 ? trimmed : `${trimmed.slice(0, 157).trimEnd()}…`;
}

function cleanGridAssetName(value: unknown): string | null {
  const cleaned = cleanFactText(value);
  if (!cleaned || /^(?:unknown|unnamed|not[\s_-]*available)[\s_-]*\d*$/i.test(cleaned)) return null;
  return cleaned;
}

function cleanVoltageClass(value: unknown): string | null {
  const cleaned = cleanFactText(value);
  if (!cleaned || /^0(?:\.0+)?\s*kV$/i.test(cleaned)) return null;
  return cleaned;
}

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function finitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatDecimal(value: number, maximumFractionDigits = 1): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value);
}

function formatEqualAwareRange(
  low: number,
  high: number,
  maximumFractionDigits = 2,
): string {
  const lowLabel = formatDecimal(low, maximumFractionDigits);
  return Math.abs(low - high) < 1e-9
    ? lowLabel
    : `${lowLabel}–${formatDecimal(high, maximumFractionDigits)}`;
}

function formatUtilizationRange(range: { low: number; high: number }): string {
  return `${formatEqualAwareRange(range.low * 100, range.high * 100)}%`;
}

function waterLoadBasisLabel(
  basis: PowerMwBasis,
  basisIsScreeningAssumption: boolean,
): string {
  switch (basis) {
    case 'it':
      return 'IT load';
    case 'facility':
      return basisIsScreeningAssumption
        ? 'facility-demand screening default (MW basis not recorded)'
        : 'facility demand';
    case 'utility-service':
      return 'utility service';
  }
}

function sedEvidenceSource<T>(
  sed: SiteEvidenceDocument,
  sectionId: string,
  label: string,
  section: Omit<TrustedSedSection<T>, 'status'> & { status: OnSiteInfrastructureStatus },
): CallReadyEvidenceSource {
  const sourceLabel = cleanFactText(section.source);
  return {
    id: `sed:${sed.id}:v${sed.version}:${sectionId}`,
    kind: section.status === 'verified-from-intent' || section.status === 'verified-from-context'
      ? 'site-record'
      : 'public-dataset',
    label: `Current SED · ${label}${sourceLabel ? ` · ${sourceLabel}` : ''}`,
    vintage: section.dataAsOf ?? section.retrievedAt,
    url: section.sourceUrl,
    locator: sectionId,
  };
}

function intentFieldIsIndependent(
  site: SiteEntity,
  field: keyof NonNullable<NonNullable<SiteEntity['projectIntent']>['fieldSource']>,
): boolean {
  return site.projectIntent?.fieldSource?.[field] !== 'prior-report';
}

export function buildCallReadyFindingsModel(
  canonicalSite: SiteEntity,
  analyses: readonly SiteRagReport[],
  options: BuildCallReadyFindingsOptions = {},
): CallReadyFindingsModel {
  const activeIntent = deriveIntentForSelectedAssessment(canonicalSite.projectIntent, canonicalSite.generationAssessmentSelection);
  const site = activeIntent === canonicalSite.projectIntent ? canonicalSite : { ...canonicalSite, projectIntent: activeIntent };
  const currentSed = exactCurrentSed(site, options.sed);
  const sedEconomicEvidence = trustedSedSection(currentSed?.economicEvidence);
  const sedWorkforceProfile = trustedSedWorkforceProfile(currentSed);
  const climateSection = trustedSedSection(currentSed?.climate);
  const latestReports = latestReportsByType(analyses, site);
  const isReportUsable = (type: ReportTypeId): boolean => {
    const report = latestReports.get(type);
    return report ? reportCanSupplyBriefEvidence(report, site) : false;
  };
  const power = effectivePower(site.projectIntent);
  const recordedPowerRange = effectivePowerMwRange(power);
  const economicScreeningDefaults = resolveEconomicScreeningSiteDefaults(site);
  const economicLoadMw = options.economicContributionEnabled
    && typeof site.economicContributionInputs?.loadMw === 'number'
    && Number.isFinite(site.economicContributionInputs.loadMw)
    && site.economicContributionInputs.loadMw > 0
      ? site.economicContributionInputs.loadMw
      : null;
  const explicitEconomicLoadRange = site.economicContributionInputs?.loadRange;
  const economicLoadRange = options.economicContributionEnabled
    && economicLoadMw == null
    && finitePositive(explicitEconomicLoadRange?.low)
    && finitePositive(explicitEconomicLoadRange?.high)
    && explicitEconomicLoadRange.high >= explicitEconomicLoadRange.low
      ? { min: explicitEconomicLoadRange.low, max: explicitEconomicLoadRange.high }
      : null;
  const powerDisplayLabel = economicLoadMw != null
    ? `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(economicLoadMw)} MW`
    : economicLoadRange != null
      ? economicLoadRange.min === economicLoadRange.max
        ? `${formatDecimal(economicLoadRange.min)} MW`
        : `${formatDecimal(economicLoadRange.min)}–${formatDecimal(economicLoadRange.max)} MW`
      : power.displayLabel;
  const powerBasisLabel = options.economicContributionEnabled && site.economicContributionInputs?.loadBasis
    ? ({ it: 'IT load', facility: 'Facility demand', 'utility-service': 'Utility service' } as const)[site.economicContributionInputs.loadBasis]
    : site.projectIntent?.powerBasis === 'it-load'
      ? 'IT load'
      : site.projectIntent?.powerBasis === 'facility'
        ? 'Facility demand'
        : 'Basis not yet recorded';
  const hasPower = economicLoadMw != null || economicLoadRange != null || power.bucket !== 'undecided';
  const economicRequest = options.economicContributionEnabled
    ? economicScreenRequestFromSite(site, { sed: currentSed })
    : null;
  const storedEconomicSnapshot = site.economicContributionSnapshot;
  const economicRequestWithResolvedLocation = economicRequest && storedEconomicSnapshot
    ? {
        ...economicRequest,
        directJobs: {
          ...economicRequest.directJobs,
          location: storedEconomicSnapshot.location,
        },
      }
    : economicRequest;
  const expectedEconomicInputHash = economicRequestWithResolvedLocation
    ? hashEconomicModelInput(economicRequestWithResolvedLocation)
    : null;
  const economicSnapshot = !options.economicContributionEnabled
    ? null
    : storedEconomicSnapshot
      && storedEconomicSnapshot.inputHash === expectedEconomicInputHash
      && (economicRequest?.waterUse == null || isCurrentWaterUseScreeningResult(
        storedEconomicSnapshot.waterUseScreening,
        { requireTmyAttempt: economicRequest.waterUse.siteCoordinates != null },
      ))
      ? storedEconomicSnapshot
      : economicRequest
        ? calculateEconomicContributionScreening(economicRequestWithResolvedLocation ?? economicRequest)
        : null;
  const economicProfile = options.economicContributionEnabled
    ? site.economicProfileSnapshot
      ?? economicSnapshot?.economicProfile
      ?? sedWorkforceProfile
      ?? null
    : null;
  const promotedImpact = resolvePromotedGenerationScenario(site.projectIntent, site.promotedGenerationScenarioSnapshot, null, site.generationAssessmentSelection, site.generationProjectInputBinding) ?? undefined;
  const stage = buildBriefStage(site, options.decisionStage, promotedImpact ?? null);
  const modelledWater = promotedImpact?.result.water;
  // P4.1 (2026-09-05) — the promoted scenario's DUTY (equipment / process
  // boundary) and the SED screen's bands (site boundary) bound different
  // volumes; neither supersedes the other (P3.4). This model used to prefer
  // the duty and SUPPRESS every screen band while the prompt policy declared
  // the screen "the sole numerical basis": two surfaces asserting opposite
  // precedence over the same question. Both are stated now — rows, summary and
  // visuals — each labelled by its basis; the prompt policy says the same.
  const hasModelledWater = modelledWater?.totalConsumptionGalPerDay != null
    || modelledWater?.totalWithdrawalGalPerDay != null;
  const waterScreen = economicSnapshot?.waterUseScreening?.status === 'calculated'
    ? economicSnapshot.waterUseScreening
    : null;
  const waterScreeningResultRaw = economicSnapshot?.waterUseScreening;
  const waterScreeningNotCalculated = waterScreeningResultRaw?.status === 'not-calculated';
  const waterScreeningReason = waterScreeningNotCalculated ? waterScreeningResultRaw?.unavailableReason : undefined;
  const modernWaterScenario = waterScreen?.scenarios.find(scenario => scenario.kind === 'modern-operator-fleet');
  const nationalWaterScenario = waterScreen?.scenarios.find(scenario => scenario.kind === 'national-current-average');
  const wetConsumptionScenario = waterScreen?.scenarios.find(scenario => scenario.kind === 'wet-cooling-tower-consumption');
  const wetWithdrawalScenario = waterScreen?.scenarios.find(scenario => scenario.kind === 'wet-cooling-tower-withdrawal');
  const savedDryCoolingScenario = waterScreen?.scenarios.find(scenario => scenario.kind === 'dry-cooling');
  const derivedDry = options.economicContributionEnabled && sedEconomicEvidence && climateSection
    ? derivedDryCoolingForSed(currentSed) : null;
  const frozenWater = sedEconomicEvidence?.data?.waterUseScreening;
  const sameDryScope = waterScreen && frozenWater && frozenWater.siteId === site.id
    && currentSed?.location.lat === site.location.lat && currentSed.location.lng === site.location.lng
    && hashEconomicModelInput([waterScreen.loadRange, waterScreen.resolvedContext?.pue, waterScreen.resolvedContext?.rackCoolingStrategy])
      === hashEconomicModelInput([frozenWater.loadRange, frozenWater.resolvedContext?.pue, frozenWater.resolvedContext?.rackCoolingStrategy]);
  const selectedDry = sameDryScope ? derivedDry : null;
  const dryCoolingScenario = savedDryCoolingScenario && selectedDry ? { ...savedDryCoolingScenario,
    dryCoolingDesignScreen: selectedDry.screen, sourceRefs: [...savedDryCoolingScenario.sourceRefs, ...selectedDry.sources],
  } : savedDryCoolingScenario;
  const adiabaticWaterScreening = dryCoolingScenario?.adiabaticWaterScreening?.status === 'calculated'
    ? dryCoolingScenario.adiabaticWaterScreening
    : null;
  const adiabaticClimateUnavailable = waterScreen?.adiabaticClimateStatus?.status === 'unavailable';
  const dryCoolingDesignScreen = dryCoolingScenario?.dryCoolingDesignScreen;
  const w45DryCheck = dryCoolingDesignScreen?.classChecks.find(check => check.waterClass === 'W45');
  const hasDryOnlyScreeningCandidate = dryCoolingDesignScreen?.classChecks.some(
    check => check.dryStatus === 'dry-only-screening-candidate',
  ) ?? false;
  const heatRejection = waterScreen?.resolvedContext?.heatRejection;
  const hybridHeatRejection = heatRejection === 'hybrid';
  const annualFacilityEnergy = waterScreen?.resolvedContext
    ? annualEnergyMwh(
        waterScreen.resolvedContext.facilityHeatMw,
        waterScreen.resolvedContext.utilization,
      )
    : null;
  const annualItEnergy = waterScreen?.resolvedContext
    ? annualEnergyMwh(
        waterScreen.resolvedContext.itLoadMw,
        waterScreen.resolvedContext.utilization,
      )
    : null;
  const waterScenarioLabel = waterScreen
    ? economicScreeningDefaults.scenarioOptions.find(
        option => option.phaseBasis === waterScreen.loadRange.phaseBasis,
      )?.label.replace(/\s+—\s+.*$/, '') ?? null
    : null;
  const hasScreenedWater = (waterScreen?.scenarios.length ?? 0) > 0;
  const hasWaterQuantity = hasModelledWater || hasScreenedWater;
  const siteSource: CallReadyEvidenceSource = {
    id: `site:${site.id}`,
    kind: 'site-record',
    label: 'Current site record',
    vintage: site.updatedAt,
  };

  const waterEvidence = evidenceForTypes(latestReports, ['water-resource'], site);
  const infrastructureEvidence = evidenceForTypes(latestReports, [
    'power-availability',
    'interconnection',
    'test-fit',
    'feasibility',
  ], site);
  const concernEvidence = evidenceForTypes(latestReports, [
    'environmental',
    'public-sentiment',
    'test-fit',
    'feasibility',
  ], site);

  const gridSection = trustedSedSection(currentSed?.grid);
  const connectivitySection = trustedSedSection(currentSed?.connectivity);
  const interconnectionSection = trustedSedSection(currentSed?.interconnection);
  const siteProfileSection = trustedSedSection(currentSed?.siteProfile);
  const parcelResolution = trustedParcelLandUseSection(currentSed?.parcel);
  const parcelSection = parcelResolution?.section ?? null;
  const environmentalDetailResult = trustedSedSectionDetail(currentSed?.environmental);
  const environmentalSection = environmentalDetailResult.status === 'trusted' ? environmentalDetailResult.section : null;
  const floodSection = trustedSedSection(currentSed?.flood);
  const waterStressSection = trustedSedSection(currentSed?.waterStress);
  const waterStressLevel = waterStressSection ? cleanFactText(waterStressSection.data.stressLevel) : null;
  const waterRiskScore = waterStressSection && finiteNonNegative(waterStressSection.data.overallWaterRisk)
    ? waterStressSection.data.overallWaterRisk
    : null;
  // Which wet-cooling case this brief is looking at. `selected` and `hybrid`
  // follow the recorded cold end. Without a recorded wet cold end, a High WRI
  // baseline water-stress screen rules a wet tower out for this site
  // (`screened-out`, the same threshold that sends Intent's heat-rejection
  // suggestion to dry coolers); a recorded dry-side design makes the wet rows
  // an `alternative`; only an unknown cold end leaves the case `open`.
  const wetCoolingCase: 'none' | 'selected' | 'hybrid' | 'open' | 'alternative' | 'screened-out' =
    !wetWithdrawalScenario && !wetConsumptionScenario
      ? 'none'
      : (wetWithdrawalScenario ?? wetConsumptionScenario)?.applicability === 'selected-technology'
        ? 'selected'
        : hybridHeatRejection
          ? 'hybrid'
          : waterStressLevel === 'High'
            ? 'screened-out'
            : heatRejection == null || heatRejection === 'unknown'
              ? 'open'
              : 'alternative';
  const wetScreenedOut = wetCoolingCase === 'screened-out';
  const wetRowsLeadPrimary = wetCoolingCase === 'selected' || wetCoolingCase === 'hybrid' || wetCoolingCase === 'open';

  const structuredInfrastructureDetails: CallReadyAnswerDetail[] = [];
  const structuredInfrastructureEvidence: CallReadyEvidenceSource[] = [];
  const infrastructureSummaryFacts: string[] = [];
  let infrastructureUsesSiteRecord = false;

  const powerIntentIndependent = economicLoadMw != null
    || economicLoadRange != null
    || (intentFieldIsIndependent(site, 'powerRequirement')
      && intentFieldIsIndependent(site, 'powerRequirementMW')
      && intentFieldIsIndependent(site, 'powerBasis'));
  const hasIndependentPowerFact = hasPower && powerIntentIndependent;
  if (hasIndependentPowerFact) {
    structuredInfrastructureDetails.push({
      label: 'Declared project load',
      value: `${powerDisplayLabel} · ${powerBasisLabel}`,
    });
    infrastructureSummaryFacts.push(powerBasisLabel === 'Basis not yet recorded'
      ? `${powerDisplayLabel}; MW basis not yet recorded`
      : `${powerDisplayLabel} ${powerBasisLabel.toLowerCase()}`);
    infrastructureUsesSiteRecord = true;
  }

  const siteAreaAcres = finitePositive(site.siteBoundaryAggregate?.totalArea)
    ? site.siteBoundaryAggregate.totalArea
    : finitePositive(site.siteArea)
      ? site.siteArea
      : null;
  const boundaryParcelCount = site.siteBoundaryAggregate?.parcelCount
    ?? site.siteBoundaries?.filter((parcel) => parcel.polygon.length >= 3).length
    ?? (site.siteBoundary && site.siteBoundary.length >= 3 ? 1 : 0);
  const sedSiteAreaAcres = siteAreaAcres == null && finitePositive(siteProfileSection?.data.acres)
    ? siteProfileSection.data.acres
    : null;
  const effectiveSiteAreaAcres = siteAreaAcres ?? sedSiteAreaAcres;
  const hasLayoutFact = effectiveSiteAreaAcres != null || boundaryParcelCount > 0;
  if (hasLayoutFact) {
    const areaText = effectiveSiteAreaAcres != null
      ? `${formatDecimal(effectiveSiteAreaAcres)} acres`
      : 'Area not calculated';
    const parcelText = boundaryParcelCount > 0
      ? `${boundaryParcelCount} mapped ${boundaryParcelCount === 1 ? 'parcel' : 'parcels'}`
      : 'parcel count unavailable';
    structuredInfrastructureDetails.push({ label: 'Mapped site envelope', value: `${areaText} · ${parcelText}` });
    infrastructureSummaryFacts.push(`${areaText} across the mapped site envelope`);
    if (siteAreaAcres != null || boundaryParcelCount > 0) infrastructureUsesSiteRecord = true;
    if (siteAreaAcres == null && sedSiteAreaAcres != null && currentSed && siteProfileSection) {
      structuredInfrastructureEvidence.push(sedEvidenceSource(currentSed, 'siteProfile', 'Site profile', siteProfileSection));
    }
  }

  let hasGridFact = false;
  if (gridSection && currentSed) {
    const substationName = cleanGridAssetName(gridSection.data.substationName);
    const voltage = cleanVoltageClass(gridSection.data.voltage);
    const utilityName = cleanFactText(gridSection.data.utilityName);
    const distanceKm = finiteNonNegative(gridSection.data.substationDistKm)
      ? gridSection.data.substationDistKm
      : null;
    const gridParts = [
      substationName,
      distanceKm != null ? `${formatDecimal(distanceKm)} km from the mapped substation` : null,
      voltage ? `${voltage} voltage class` : null,
      utilityName,
    ].filter((part): part is string => part != null);
    if (gridParts.length > 0) {
      structuredInfrastructureDetails.push({
        label: 'Grid proximity screen',
        value: `${gridParts.join(' · ')}. Proximity and voltage class do not confirm firm capacity or upgrade scope.`,
      });
      infrastructureSummaryFacts.push(
        substationName
          ? `${substationName}${distanceKm != null ? ` at ${formatDecimal(distanceKm)} km` : ''}`
          : `a mapped grid asset${distanceKm != null ? ` at ${formatDecimal(distanceKm)} km` : ''}`,
      );
      structuredInfrastructureEvidence.push(sedEvidenceSource(currentSed, 'grid', 'Grid infrastructure', gridSection));
      hasGridFact = true;
    }
  }

  const isIslandedArchitecture = isFullyIslandedIntent(site.projectIntent, site.typedPreferencesDefaults?.['power-availability']?.btmStrategy);

  let hasInterconnectionFact = false;
  if (isIslandedArchitecture) {
    structuredInfrastructureDetails.push({
      label: 'Power interconnection architecture',
      value: 'The declared plan is fully islanded, without a utility grid connection. Grid service and export queues are outside this planned topology; fuel, reliability, environmental permits and any later grid tie require separate diligence.',
    });
    infrastructureSummaryFacts.push('a declared islanded (off-grid) power plan');
    hasInterconnectionFact = true;
  } else {
    const liveInterconnection = intentFieldIsIndependent(site, 'interconnectionStatus')
      ? site.projectIntent?.interconnectionStatus
      : undefined;
    if (liveInterconnection?.queueStatus && liveInterconnection.queueStatus !== 'none') {
    const interconnectionParts = [
      liveInterconnection.queueStatus === 'executed-load-service'
        ? INTERCONNECTION_STATUS_LABELS[liveInterconnection.queueStatus]
        : liveInterconnection.queueStatus.replaceAll('-', ' '),
      cleanFactText(liveInterconnection.queueId),
      liveInterconnection.studyPhase ? `${liveInterconnection.studyPhase.replaceAll('-', ' ')} study${liveInterconnection.queueStatus === 'executed-load-service' ? ' context' : ''}` : null,
    ].filter((part): part is string => part != null);
    structuredInfrastructureDetails.push({
      label: liveInterconnection.queueStatus === 'executed-load-service' ? 'Declared utility service milestone' : 'Declared interconnection position',
      value: interconnectionParts.join(' · ') + (liveInterconnection.queueStatus === 'executed-load-service'
        ? '. Service works and commissioning remain separate; any generator interconnection requires its own evidence.' : ''),
    });
    infrastructureSummaryFacts.push(liveInterconnection.queueStatus === 'executed-load-service'
      ? `a customer load-service agreement recorded as accepted (${interconnectionParts.join(', ')}), with service works and commissioning still separate`
      : `an interconnection position recorded as ${interconnectionParts.join(', ')}`);
    infrastructureUsesSiteRecord = true;
    hasInterconnectionFact = true;
  } else if (interconnectionSection && currentSed) {
    const queueStatus = interconnectionSection.data.queueStatus;
    const iso = interconnectionSection.data.iso;
    const queueId = cleanFactText(interconnectionSection.data.queueId);
    const interconnectionParts = [
      iso,
      queueStatus && queueStatus !== 'none'
        ? queueStatus === 'executed-load-service' ? INTERCONNECTION_STATUS_LABELS[queueStatus] : queueStatus.replaceAll('-', ' ')
        : null,
      queueId,
      interconnectionSection.data.studyPhase
        ? `${interconnectionSection.data.studyPhase.replaceAll('-', ' ')} study${queueStatus === 'executed-load-service' ? ' context' : ''}`
        : null,
    ].filter((part): part is string => part != null);
    if (interconnectionParts.length > 0) {
      structuredInfrastructureDetails.push({
        label: queueStatus === 'executed-load-service' ? 'Declared utility service milestone' : 'Interconnection record',
        value: `${interconnectionParts.join(' · ')}. ${queueStatus === 'executed-load-service'
          ? 'This records an accepted customer load-service agreement; service works and commissioning remain separate and any generator interconnection requires its own evidence.'
          : 'This records queue context, not an executed service commitment.'}`,
      });
      infrastructureSummaryFacts.push(queueStatus === 'executed-load-service'
        ? `a customer load-service agreement recorded as accepted (${interconnectionParts.join(', ')}), with service works and commissioning still separate`
        : `interconnection context in ${interconnectionParts.join(', ')}`);
      structuredInfrastructureEvidence.push(sedEvidenceSource(currentSed, 'interconnection', 'Interconnection', interconnectionSection));
      hasInterconnectionFact = true;
    }
  }
}

  let hasConnectivityFact = false;
  if (connectivitySection && currentSed) {
    const fiberDistanceKm = finiteNonNegative(connectivitySection.data.nearestFiberKm)
      ? connectivitySection.data.nearestFiberKm
      : null;
    const fiberProvider = cleanFactText(connectivitySection.data.fiberProvider);
    const ixName = connectivitySection.data.nearestIxKind !== 'colo-fallback'
      ? cleanFactText(connectivitySection.data.nearestIxName)
      : null;
    const ixDistanceKm = ixName && finiteNonNegative(connectivitySection.data.nearestIxDistKm)
      ? connectivitySection.data.nearestIxDistKm
      : null;
    if (fiberDistanceKm != null) {
      structuredInfrastructureDetails.push({
        label: 'Fiber-corridor screen',
        value: `${fiberProvider ?? 'Mapped fiber corridor'} · ${formatDecimal(fiberDistanceKm)} km. Corridor proximity does not confirm service, capacity, or route diversity.`,
      });
      infrastructureSummaryFacts.push(`a mapped fiber corridor at ${formatDecimal(fiberDistanceKm)} km`);
      hasConnectivityFact = true;
    } else if (ixName) {
      structuredInfrastructureDetails.push({
        label: 'Connectivity proximity screen',
        value: `${ixName}${ixDistanceKm != null ? ` · ${formatDecimal(ixDistanceKm)} km` : ''}. IXP proximity does not confirm carrier service or diverse routes to the parcel.`,
      });
      infrastructureSummaryFacts.push(`${ixName}${ixDistanceKm != null ? ` at ${formatDecimal(ixDistanceKm)} km` : ''}`);
      hasConnectivityFact = true;
    }
    const buildDistanceFt = connectivitySection.data.fiberBuildDistanceFt;
    if (finiteNonNegative(buildDistanceFt)) {
      structuredInfrastructureDetails.push({
        label: 'Fiber build to handoff',
        value: `${formatDecimal(buildDistanceFt)} ft source-stated construction distance. Separate from corridor proximity and full route length; does not confirm carrier service or diverse routes.`,
      });
      infrastructureSummaryFacts.push(`a source-stated fiber build to handoff of ${formatDecimal(buildDistanceFt)} ft`);
      hasConnectivityFact = true;
    }
    if (hasConnectivityFact) {
      structuredInfrastructureEvidence.push(sedEvidenceSource(currentSed, 'connectivity', 'Connectivity', connectivitySection));
    }
  }

  const coolingParts = [
    intentFieldIsIndependent(site, 'coolingStrategy')
      && site.projectIntent?.coolingStrategy
      && site.projectIntent.coolingStrategy !== 'undecided'
      ? `${site.projectIntent.coolingStrategy.replaceAll('-', ' ')} rack cooling`
      : null,
    intentFieldIsIndependent(site, 'bopHeatRejection')
      && site.projectIntent?.bopHeatRejection
      && site.projectIntent.bopHeatRejection !== 'unknown'
      ? `${site.projectIntent.bopHeatRejection.replaceAll('-', ' ')} heat rejection`
      : null,
  ].filter((part): part is string => part != null);
  if (coolingParts.length > 0) {
    structuredInfrastructureDetails.push({ label: 'Declared cooling configuration', value: coolingParts.join(' · ') });
    infrastructureSummaryFacts.push(coolingParts.join(' with '));
    infrastructureUsesSiteRecord = true;
  }

  if (
    siteProfileSection
    && currentSed
    && siteProfileSection.data.slopeMethod === 'topography-grid'
    && finiteNonNegative(siteProfileSection.data.slopePercent)
  ) {
    structuredInfrastructureDetails.push({
      label: 'Terrain screen',
      value: `${formatDecimal(siteProfileSection.data.slopePercent, 2)}% sampled slope · screening terrain, not a civil grading design`,
    });
    if (!structuredInfrastructureEvidence.some((source) => source.locator === 'siteProfile')) {
      structuredInfrastructureEvidence.push(sedEvidenceSource(currentSed, 'siteProfile', 'Site profile', siteProfileSection));
    }
  }

  const infrastructurePowerCovered = hasIndependentPowerFact || isReportUsable('power-availability');
  const infrastructureGridCovered = isIslandedArchitecture || hasGridFact || hasInterconnectionFact || isReportUsable('power-availability');
  const infrastructureConnectivityCovered = hasConnectivityFact || isReportUsable('interconnection');
  const infrastructureLayoutCovered = hasLayoutFact || isReportUsable('test-fit') || isReportUsable('feasibility');
  const infrastructureSignalCount = [
    infrastructurePowerCovered,
    infrastructureGridCovered,
    infrastructureConnectivityCovered,
    infrastructureLayoutCovered,
  ].filter(Boolean).length;

  const communitySedDetails: CallReadyAnswerDetail[] = [];
  const communitySedEvidence: CallReadyEvidenceSource[] = [];
  const communityCoverageLabels: string[] = [];
  const communitySummaryFacts: string[] = [];

  if (parcelSection && currentSed) {
    const zoning = cleanFactText(parcelSection.data.zoning);
    const landUse = cleanFactText(parcelSection.data.landUse);
    const parcelParts = [
      zoning ? `zoning: ${zoning}` : null,
      landUse ? `land use: ${landUse}` : null,
      parcelResolution?.acreageTrusted && finitePositive(parcelSection.data.acres)
        ? `${formatDecimal(parcelSection.data.acres)} acres`
        : null,
    ].filter((part): part is string => part != null);
    if (parcelParts.length > 0) {
      communitySedDetails.push({
        label: 'Land-use screen',
        value: `${parcelParts.join(' · ')}. This is recorded screening context, not a rezoning or entitlement conclusion.`,
      });
      communitySedEvidence.push(sedEvidenceSource(currentSed, 'parcel', 'Parcel and land use', parcelSection));
      communityCoverageLabels.push('land use and zoning');
      communitySummaryFacts.push(`recorded ${parcelParts.join(' and ')}`);
    }
  }

  if (environmentalSection && currentSed) {
    // `undefined` on these flags means the source screen did not complete —
    // the provider was unavailable, the query failed, or a parcel could not be
    // covered (see EnvironmentalEvidence in src/types/siteEvidence.ts). Reading
    // that as `false` turns missing data into a favourable finding, and this is
    // the sentence an operator reads to an AHJ on a call. Say "not assessed".
    const habitatAssessed = environmentalSection.data.hasCriticalHabitat !== undefined;
    const wetlandsAssessed = environmentalSection.data.hasWetlands !== undefined;
    const environmentalScreenComplete = habitatAssessed && wetlandsAssessed;
    const environmentalParts = [
      !habitatAssessed
        ? 'critical habitat not assessed — the public screen did not complete'
        : environmentalSection.data.hasCriticalHabitat
          ? 'critical-habitat overlap identified'
          : 'no critical-habitat overlap identified by the current public screen',
      !wetlandsAssessed
        ? 'wetlands not assessed — the public screen did not complete'
        : environmentalSection.data.hasWetlands
          ? `wetlands identified${finitePositive(environmentalSection.data.wetlandAcres) ? ` (${formatDecimal(environmentalSection.data.wetlandAcres)} acres)` : ''}`
          : 'no wetlands identified by the current public screen',
      (environmentalSection.data.protectedAreas?.onParcel.length ?? 0) > 0
        ? `${environmentalSection.data.protectedAreas!.onParcel.length} protected-area ${environmentalSection.data.protectedAreas!.onParcel.length === 1 ? 'record' : 'records'} on parcel`
        : null,
      environmentalSection.data.tribalLands?.onParcel
        ? 'tribal/native-land overlap identified'
        : null,
      environmentalSection.data.regulatedFacilities
        ? `${environmentalSection.data.regulatedFacilities.within3MiCount} EPA-regulated ${environmentalSection.data.regulatedFacilities.within3MiCount === 1 ? 'facility' : 'facilities'} within 3 miles`
        : null,
    ].filter((part): part is string => part != null);
    communitySedDetails.push({
      label: 'Public environmental screen',
      value: environmentalParts.join(' · '),
    });
    communitySedEvidence.push(sedEvidenceSource(currentSed, 'environmental', 'Environmental screening', environmentalSection));
    // An incomplete screen is not coverage: it must not count toward the two
    // topics that make `communityScreeningReady` true.
    if (environmentalScreenComplete) communityCoverageLabels.push('environmental constraints');
    const environmentalSummaryParts = [
      environmentalSection.data.hasCriticalHabitat
        ? 'critical-habitat overlap'
        : null,
      environmentalSection.data.hasWetlands
        ? `${finitePositive(environmentalSection.data.wetlandAcres) ? `${formatDecimal(environmentalSection.data.wetlandAcres)} acres of ` : ''}mapped wetlands`
        : null,
      (environmentalSection.data.protectedAreas?.onParcel.length ?? 0) > 0
        ? `${environmentalSection.data.protectedAreas!.onParcel.length} protected-area ${environmentalSection.data.protectedAreas!.onParcel.length === 1 ? 'record' : 'records'} on parcel`
        : null,
      environmentalSection.data.tribalLands?.onParcel
        ? 'tribal/native-land overlap'
        : null,
      (environmentalSection.data.regulatedFacilities?.within3MiCount ?? 0) > 0
        ? `${environmentalSection.data.regulatedFacilities!.within3MiCount} EPA-regulated ${environmentalSection.data.regulatedFacilities!.within3MiCount === 1 ? 'facility' : 'facilities'} within 3 miles`
        : null,
    ].filter((part): part is string => part != null);
    const environmentalUnassessed = [
      habitatAssessed ? null : 'critical habitat',
      wetlandsAssessed ? null : 'wetlands',
    ].filter((part): part is string => part != null);
    communitySummaryFacts.push(
      environmentalSummaryParts.length > 0
        ? `${environmentalSummaryParts.join(' and ')}${environmentalUnassessed.length > 0 ? ` (${environmentalUnassessed.join(' and ')} not assessed — the public screen did not complete)` : ''}`
        : environmentalUnassessed.length > 0
          ? `${environmentalUnassessed.join(' and ')} not assessed — the public screen did not complete, so no overlap conclusion is available`
          : 'no mapped critical-habitat or wetland overlap in the current public screen',
    );
  } else if (currentSed) {
    if (environmentalDetailResult.status === 'failed') {
      const reasonText = `critical habitat and wetlands failed to resolve — ${environmentalDetailResult.reason}`;
      communitySedDetails.push({
        label: 'Public environmental screen',
        value: reasonText,
      });
      communitySummaryFacts.push(`environmental constraints failed (${environmentalDetailResult.reason})`);
    } else {
      const reasonText = `critical habitat and wetlands unassessed — no environmental evidence has been collected.`;
      communitySedDetails.push({
        label: 'Public environmental screen',
        value: reasonText,
      });
      communitySummaryFacts.push(`environmental constraints unassessed`);
    }
  }

  if (floodSection && currentSed) {
    const floodZone = cleanFactText(floodSection.data.floodZone);
    const floodRisk = cleanFactText(floodSection.data.floodRisk);
    const floodParts = [
      floodZone ? `zone ${floodZone}` : null,
      floodRisk ? `${floodRisk.toLowerCase()} screening risk` : null,
      floodSection.data.sfha ? 'SFHA overlap identified' : 'no SFHA overlap identified by the current FEMA screen',
    ].filter((part): part is string => part != null);
    communitySedDetails.push({ label: 'Flood screen', value: floodParts.join(' · ') });
    communitySedEvidence.push(sedEvidenceSource(currentSed, 'flood', 'Flood screening', floodSection));
    communityCoverageLabels.push('flood exposure');
    communitySummaryFacts.push(
      `${floodRisk ? `${floodRisk.toLowerCase()} flood risk on the current FEMA screen` : 'current FEMA flood screening'}${floodSection.data.sfha ? ' with SFHA overlap' : ' with no SFHA overlap identified'}`,
    );
  }

  if (waterStressSection && currentSed) {
    const stressLabel = cleanFactText(waterStressSection.data.bwsLabel);
    const stressLevel = cleanFactText(waterStressSection.data.stressLevel);
    const waterStressParts = [
      stressLabel,
      stressLevel ? `${stressLevel.toLowerCase()} screening level` : null,
      finiteNonNegative(waterStressSection.data.overallWaterRisk)
        ? `overall water-risk score ${formatDecimal(waterStressSection.data.overallWaterRisk, 2)}/5`
        : null,
    ].filter((part): part is string => part != null);
    if (waterStressParts.length > 0) {
      communitySedDetails.push({ label: 'Water-stress screen', value: waterStressParts.join(' · ') });
      communitySedEvidence.push(sedEvidenceSource(currentSed, 'waterStress', 'Water stress', waterStressSection));
      communityCoverageLabels.push('water stress');
      const conciseStressLabel = stressLabel?.replace(/^\d+\.\s*/, '');
      communitySummaryFacts.push(
        `${conciseStressLabel ?? stressLevel ?? 'Published'} baseline water stress${stressLevel ? ` (${stressLevel.toLowerCase()} screening level)` : ''}`,
      );
    }
  }

  const hasStructuredCommunityContext = Boolean(environmentalSection || floodSection || waterStressSection);
  const communityNoiseEmissionsCovered = promotedImpact != null || isReportUsable('environmental');
  const communityEnvironmentalCovered = hasStructuredCommunityContext || isReportUsable('environmental');
  const communityLandVisualCovered = communitySedEvidence.some((source) => source.locator === 'parcel')
    || isReportUsable('test-fit')
    || isReportUsable('feasibility');
  const communitySentimentCovered = isReportUsable('public-sentiment');
  const communitySignalCount = [
    communityNoiseEmissionsCovered,
    communityEnvironmentalCovered,
    communityLandVisualCovered,
    communitySentimentCovered,
  ].filter(Boolean).length;
  const communityEvidenceTopics = new Set(communityCoverageLabels);
  if (promotedImpact) communityEvidenceTopics.add('noise and emissions');
  if (isReportUsable('environmental')) communityEvidenceTopics.add('environmental constraints');
  if (isReportUsable('public-sentiment')) communityEvidenceTopics.add('public sentiment');
  if (isReportUsable('test-fit') || isReportUsable('feasibility')) {
    communityEvidenceTopics.add('land use, traffic, and visual effects');
  }
  const communityEvidenceTopicCount = communityEvidenceTopics.size;
  // This question asks what is presently identified, not whether every later
  // diligence topic is complete. Two independently grounded topic families
  // support a usable screening answer; unresolved optional topics remain
  // explicit in the copy and never become an implied all-clear.
  const communityScreeningReady = communityEvidenceTopicCount >= 2;
  const communityUnresolvedTopics = [
    !communityNoiseEmissionsCovered ? 'project-specific noise and emissions' : null,
    !communityLandVisualCovered ? 'traffic and visual effects' : null,
    !communitySentimentCovered ? 'public sentiment' : null,
  ].filter((topic): topic is string => topic != null);
  const communityUnresolvedLabel = communityUnresolvedTopics.length <= 1
    ? (communityUnresolvedTopics[0] ?? '')
    : communityUnresolvedTopics.length === 2
      ? communityUnresolvedTopics.join(' and ')
      : `${communityUnresolvedTopics.slice(0, -1).join(', ')}, and ${communityUnresolvedTopics.at(-1)}`;

  const copyApproved = isCallReadyCopyApproved();
  const copySource: CallReadyEvidenceSource = {
    id: `copy:${CALL_READY_COPY_GOVERNANCE.version}`,
    kind: 'copy-governance',
    label: copyApproved
      ? `Policy-validated industry context · ${CALL_READY_COPY_GOVERNANCE.version}`
      : `Draft industry context · ${CALL_READY_COPY_GOVERNANCE.version}`,
  };
  const impactSource: CallReadyEvidenceSource | null = promotedImpact ? {
    id: `generation-impact:${promotedImpact.id}:${promotedImpact.resultHash}`,
    kind: 'model-result',
    label: `Promoted Generation & Impact scenario · ${promotedImpact.name}`,
    vintage: promotedImpact.updatedAt,
  } : null;
  const waterScreenSources = [
    ...(modernWaterScenario?.sourceRefs ?? []),
    ...(nationalWaterScenario?.sourceRefs ?? []),
    ...(wetConsumptionScenario?.sourceRefs ?? []),
    ...(wetWithdrawalScenario?.sourceRefs ?? []),
    ...(dryCoolingScenario?.sourceRefs ?? []),
  ].reduce<CallReadyEvidenceSource[]>((sources, source) => {
    const sourceId = `water-source:${source.id}`;
    if (sources.some(candidate => candidate.id === sourceId && candidate.locator === source.locator)) return sources;
    sources.push({
      id: sourceId,
      kind: 'model-result',
      label: `${source.publisher} · ${source.title}`,
      vintage: source.publicationDate,
      url: source.url,
      excerpt: source.excerpt,
      locator: source.locator,
      evidenceRole: source.evidenceRole,
    });
    return sources;
  }, []);
  const adiabaticWaterSources: CallReadyEvidenceSource[] = adiabaticWaterScreening
      ? adiabaticWaterScreening.provenance.sources.map((source) => ({
          id: `adiabatic-water-source:${source.id}`,
          kind: 'model-result' as const,
          label: `${source.publisher} · ${source.title}`,
          url: source.url,
          locator: source.locator,
        }))
      : [];
  const waterClimateEvidence = currentSed
    && climateSection
    && waterScreen?.resolvedContext?.ambientConditions
      ? [sedEvidenceSource(currentSed, 'climate', 'Local climate', climateSection)]
      : [];

  // The declared load is stated once, in the lead's "DC power" line; the
  // water rows below carry only the quantities and their screening basis.
  const waterDetails: CallReadyAnswerDetail[] = [
    ...(waterScreeningNotCalculated && waterScreeningReason
      ? [{ label: 'Water-use screening status', value: `Failed — ${waterScreeningReason}` }]
      : []),
    ...(waterScenarioLabel
      ? [{ label: 'Screening scope', value: waterScenarioLabel.replace(/^Whole project/, 'Whole-project build-out') }]
      : []),
    ...(annualFacilityEnergy
      ? [{ label: 'Annual facility energy', value: formatAnnualEnergy(annualFacilityEnergy) }]
      : []),
    ...(annualItEnergy
      ? [{ label: 'Annual IT energy', value: formatAnnualEnergy(annualItEnergy) }]
      : []),
    ...(modelledWater?.totalConsumptionGalPerDay != null
      ? [{ label: 'Modeled peak total consumption · promoted scenario duty · equipment/process boundary', value: formatWhole(modelledWater.totalConsumptionGalPerDay, 'gal/day') }]
      : []),
    ...(modelledWater?.totalWithdrawalGalPerDay != null
      ? [{ label: 'Modeled peak total withdrawal · promoted scenario duty · equipment/process boundary', value: formatWhole(modelledWater.totalWithdrawalGalPerDay, 'gal/day') }]
      : []),
    ...(modernWaterScenario
      ? [{
          label: modernWaterScenario.flowBasis === 'unspecified'
            ? 'Total site water · site boundary · modern operator benchmark · flow basis not specified · average day'
            : 'Total site water · site boundary · modern operator benchmark · average day',
          value: `${formatWaterRate(modernWaterScenario.averageGallonsPerDay)} · ${formatAnnualWater(modernWaterScenario.annualGallons)}`,
        }]
      : []),
    ...(nationalWaterScenario
      ? [{
          label: 'Total site water · site boundary · LBNL 2023 national average · average day',
          value: `${formatWaterRate(nationalWaterScenario.averageGallonsPerDay)} · ${formatAnnualWater(nationalWaterScenario.annualGallons)}`,
        }]
      : []),
    ...(dryCoolingScenario
      ? [{
          label: dryCoolingScenario.applicability === 'selected-technology'
            ? 'Potential dry-mode cooling-process water'
            : hybridHeatRejection
              ? 'Potential hybrid dry-mode cooling-process water'
              : 'Potential dry-mode cooling-process sensitivity',
          value: hasDryOnlyScreeningCandidate
            ? `Potentially ${formatWaterRate(dryCoolingScenario.averageGallonsPerDay)} only while the plant can carry the full duty in dry mode${w45DryCheck ? `; the W45 temperature screen is ${dryCoolingCapacityStatusLabel(w45DryCheck.dryStatus)}` : ''}. Capacity remains vendor-unverified, and this is not total-site water.`
            // `hasDryOnlyScreeningCandidate` collapses "a screen ran and no
            // class qualified" with "no screen exists" (`?? false`), but the
            // scenario itself renders whenever heatRejection is unknown — the
            // common minimum-input site. Attributing the negative result to a
            // "published temperature screen" that was never run cites evidence
            // that does not exist, which the project's sourcing rule forbids.
            // Withhold the number either way; only claim the screen when there
            // is one.
            : dryCoolingDesignScreen
              ? `No numeric dry-only water endpoint is shown because the published temperature screen does not establish full-duty dry capacity${w45DryCheck ? `; the W45 result is ${dryCoolingCapacityStatusLabel(w45DryCheck.dryStatus)}` : ''}. This is not evidence of zero total-site water.`
              : 'No numeric dry-only water endpoint is shown because no published dry-design capacity screen is available for this site. This is not evidence of zero total-site water.',
        }]
      : []),
    ...(dryCoolingDesignScreen ? [{
      label: 'Published dry-design capacity screen',
      value: `SED peak dry-bulb proxy ${formatDecimal(dryCoolingDesignScreen.sitePeakDryBulbProxyC)}°C · full declared heat duty ${formatMwBand(dryCoolingDesignScreen.screeningDutyMw)} · ${formatDecimal(dryCoolingDesignScreen.dryCoolerApproachC.low)}–${formatDecimal(dryCoolingDesignScreen.dryCoolerApproachC.high)}°C dry-cooler approach plus ${formatDecimal(dryCoolingDesignScreen.seriesHeatExchangerApproachC.low)}–${formatDecimal(dryCoolingDesignScreen.seriesHeatExchangerApproachC.high)}°C CDU/series-HX allowance (${formatDecimal(dryCoolingDesignScreen.totalAmbientToFacilitySupplyApproachC.low)}–${formatDecimal(dryCoolingDesignScreen.totalAmbientToFacilitySupplyApproachC.high)}°C total stack) · ${dryCoolingClassSummary(dryCoolingDesignScreen, 'dry')}. This is a temperature-headroom screen, not an OEM MW-capacity result.${selectedDry?.paired ? ` ${tmyHottestHoursBasisText(selectedDry.paired)} ${NONCOINCIDENT_DRY_BULB_LIMITATION}` : ''}`,
    }] : []),
    ...(adiabaticWaterScreening ? [{
      label: 'Adiabatic / hybrid assist water',
      value: `${formatWaterRate(adiabaticWaterScreening.averageDailyMakeupGallons)} annual-average makeup · ${formatWaterRate(adiabaticWaterScreening.peakDailyMakeupGallons)} TMY peak-day makeup · ${formatAnnualWater(adiabaticWaterScreening.annualMakeupGallons)} · ${formatHoursRange(adiabaticWaterScreening.activeHours)} active. Site-coordinate-selected, nearest-grid 8,760-hour TMY with disclosed industry screening assumptions; equipment selection and the final control sequence can narrow this range.`,
    }] : dryCoolingScenario ? [{
      label: 'Adiabatic / hybrid assist water',
      value: adiabaticClimateUnavailable
        ? 'Site-coordinate hourly weather could not be loaded for the last run. Retry the screening calculation; monthly climate is not substituted for an annual estimate.'
        : dryCoolingDesignScreen
        ? `${Math.round(dryCoolingDesignScreen.adiabaticSaturationEffectiveness.low * 100)}%–${Math.round(dryCoolingDesignScreen.adiabaticSaturationEffectiveness.high * 100)}% pad-effectiveness temperature screen · ${dryCoolingClassSummary(dryCoolingDesignScreen, 'adiabatic')}. Recalculate this screening scenario to load site-coordinate-selected hourly TMY weather and estimate annual assist water.`
        : 'Recalculate this screening scenario to load site-coordinate-selected hourly weather and estimate adiabatic-assist water. Equipment selection is still required to prove full-duty capacity.',
    }] : []),
    ...(wetConsumptionScenario
      ? [{
          label: wetConsumptionScenario.applicability === 'selected-technology'
            ? 'Wet-tower consumption · selected technology · average day'
            : hybridHeatRejection
              ? 'Hybrid wet-mode consumption endpoint · average day'
              : wetScreenedOut
                ? 'Wet-tower consumption · screened out by High water stress · average day'
                : 'Wet-tower consumption sensitivity · average day',
          value: `${formatWaterRate(wetConsumptionScenario.averageGallonsPerDay)} · ${formatAnnualWater(wetConsumptionScenario.annualGallons)}${wetConsumptionScenario.fullLoadGallonsPerDay ? ` · ${formatWaterRate(wetConsumptionScenario.fullLoadGallonsPerDay)} at 100% load` : ''}`,
        }]
      : []),
    ...(wetWithdrawalScenario
      ? [{
          label: wetWithdrawalScenario.applicability === 'selected-technology'
            ? 'Wet-tower makeup/withdrawal · selected technology · average day'
            : hybridHeatRejection
              ? 'Hybrid wet-mode makeup/withdrawal endpoint · average day'
              : wetScreenedOut
                ? 'Wet-tower makeup/withdrawal · screened out by High water stress · average day'
                : 'Wet-tower makeup/withdrawal sensitivity · average day',
          value: `${formatWaterRate(wetWithdrawalScenario.averageGallonsPerDay)} · ${formatAnnualWater(wetWithdrawalScenario.annualGallons)}${wetWithdrawalScenario.fullLoadGallonsPerDay ? ` · ${formatWaterRate(wetWithdrawalScenario.fullLoadGallonsPerDay)} at 100% load` : ''}`,
        }]
      : []),
    ...(hybridHeatRejection
      ? [{
          label: 'Hybrid blended case',
          value: 'Not calculated until the expected wet-mode operating fraction is recorded; dry and wet endpoints remain separate.',
        }]
      : []),
    ...(heatRejection === 'once-through'
      ? [{
          label: 'Once-through selected design',
          value: 'Not quantified without site-specific withdrawal and return-flow factors; no consumption value is fabricated.',
        }]
      : []),
    ...(waterScreen?.resolvedContext ? [
      { label: 'IT load used by WUE screen', value: formatMwBand(waterScreen.resolvedContext.itLoadMw) },
      { label: 'Facility heat rejected', value: `${formatMwBand(waterScreen.resolvedContext.facilityHeatMw)} at full declared load; annual bands apply the utilization range below` },
      {
        label: 'PUE used',
        value: `${formatEqualAwareRange(waterScreen.resolvedContext.pue.low, waterScreen.resolvedContext.pue.high)} · ${waterScreen.resolvedContext.pue.sourceLabel ?? waterScreen.resolvedContext.pue.source.replaceAll('-', ' ')}`,
      },
      {
        label: 'Annual utilization / load factor',
        value: `${formatUtilizationRange(waterScreen.resolvedContext.utilization)} · ${waterScreen.resolvedContext.utilization.sourceLabel ?? waterScreen.resolvedContext.utilization.source.replaceAll('-', ' ')}`,
      },
      {
        // The cold end the water screen ran on — an input, not the lead's
        // recommendation (which may name a cold end the record does not yet).
        label: 'Cold end used by the water screen',
        value: waterScreen.resolvedContext.heatRejection === 'unknown'
          ? 'not recorded (cooling rows are alternatives)'
          : waterScreen.resolvedContext.heatRejection.replaceAll('-', ' '),
      },
      ...(waterScreen.resolvedContext.operatingModel
        ? [{ label: 'Data-center operating model', value: waterScreen.resolvedContext.operatingModel.replaceAll('-', ' ') }]
        : []),
      ...(waterScreen.resolvedContext.workload
        ? [{ label: 'Workload / heat-density context', value: waterScreen.resolvedContext.workload.replaceAll('-', ' ') }]
        : []),
      ...(waterScreen.resolvedContext.rackCoolingStrategy
        ? [{ label: 'IT cooling loop', value: waterScreen.resolvedContext.rackCoolingStrategy.replaceAll('-', ' ') }]
        : []),
      ...(waterScreen.resolvedContext.ambientConditions ? [{
        label: 'Local ambient conditions',
        value: `${formatDecimal(waterScreen.resolvedContext.ambientConditions.averageDryBulbC)}°C average / ${formatDecimal(waterScreen.resolvedContext.ambientConditions.peakDryBulbC)}°C SED peak dry-bulb proxy · ${formatDecimal(waterScreen.resolvedContext.ambientConditions.averageWetBulbC)}°C average / ${formatDecimal(waterScreen.resolvedContext.ambientConditions.designWetBulbC)}°C ${WET_BULB_BASIS_LABELS[waterScreen.resolvedContext.ambientConditions.wetBulbBasis]} screening wet bulb · ${formatDecimal(waterScreen.resolvedContext.ambientConditions.relativeHumidityPct)}% RH`,
      }, {
        label: 'Climate treatment',
        value: adiabaticWaterScreening
          ? `Site-coordinate-selected, nearest-grid ${adiabaticWaterScreening.provenance.tmy.hourCount.toLocaleString('en-US')}-hour TMY drives the adiabatic makeup and active-hours ranges. The published temperature-headroom screen remains separate from vendor capacity certification.`
          : dryCoolingDesignScreen
          ? 'Applied to the published-range dry/adiabatic temperature-headroom screen. It is not converted into a manufacturer derate, hourly economizer schedule, or annual hybrid water quantity.'
          : 'Applied to cooling-suitability disclosure; not converted into an hourly economizer or hybrid wet-mode adjustment without equipment curves, set points, and an operating schedule.',
      }] : [{
        label: 'Climate treatment',
        value: adiabaticWaterScreening
          ? `Site-coordinate-selected, nearest-grid ${adiabaticWaterScreening.provenance.tmy.hourCount.toLocaleString('en-US')}-hour TMY drives the adiabatic-assist range. Monthly SED climate is not substituted for hourly weather.`
          : 'No trusted exact-current SED climate was available; the quantities use cited industry screening benchmarks.',
      }]),
    ] : []),
    ...sourceDetails(waterEvidence),
  ];
  const waterCalculationBasis = waterScreen?.resolvedContext
    ? [
        waterScenarioLabel?.replace(/^Whole project/, 'Whole-project build-out'),
        waterScreen.loadRange.low === waterScreen.loadRange.high
          ? `${formatDecimal(waterScreen.loadRange.low)} MW ${waterLoadBasisLabel(waterScreen.loadRange.basis, waterScreen.loadRange.basisSource === 'screening-assumption')}`
          : `${formatDecimal(waterScreen.loadRange.low)}–${formatDecimal(waterScreen.loadRange.high)} MW ${waterLoadBasisLabel(waterScreen.loadRange.basis, waterScreen.loadRange.basisSource === 'screening-assumption')}`,
        `${formatUtilizationRange(waterScreen.resolvedContext.utilization)} utilization`,
        `PUE ${formatEqualAwareRange(waterScreen.resolvedContext.pue.low, waterScreen.resolvedContext.pue.high)}`,
      ].filter((part): part is string => Boolean(part)).join(' · ')
    : null;
  // P4.1 — the promoted scenario's duty (equipment/process boundary) and the
  // screen's bands (site boundary) are different quantities; neither
  // supersedes the other, and the basis row below says so.
  const dutyBasisNote = hasModelledWater && hasScreenedWater
    ? 'The promoted scenario duty (equipment/process boundary) and the site-boundary screening bands are different quantities; neither supersedes the other.'
    : null;
  // WATER AT A GLANCE — kept one row down in the power & cooling answer.
  //
  // Live 2026-09-13: on an introductory call with the Beauregard Parish AHJ —
  // who both permit the land and own it — the customer was asked "how much
  // water" and could not answer; the band was thirty lines down a screening
  // dump. The Brief now leads with the power & cooling recommendation (owner
  // request, 2026-09-30), so the water position moves into Details — but as
  // its FIRST row, one sentence, so it can still be read out on a call.
  //
  // WITHDRAWAL leads, not consumption: an AHJ and a utility allocate what you
  // TAKE from the system. Consumption follows on the same line because the two
  // are routinely confused and the difference is most of the number. The
  // qualifier is not optional — this is a modelled screening band against a
  // declared power range, not a permitted, requested or measured withdrawal,
  // and it must never be quoted as one.
  //
  // Composed ONLY from scenario objects already on the screen: no arithmetic,
  // no interpolation, no midpoint, and no single "the" number.
  const wetWithdrawalLine = wetWithdrawalScenario
    ? `${formatWaterRate(wetWithdrawalScenario.averageGallonsPerDay)} withdrawal on an average day${wetConsumptionScenario ? ` (${formatWaterRate(wetConsumptionScenario.averageGallonsPerDay)} of it consumed)` : ''}`
    : null;
  const wetDemandLine = wetWithdrawalLine
    ?? (wetConsumptionScenario ? `${formatWaterRate(wetConsumptionScenario.averageGallonsPerDay)} consumption on an average day` : null);
  const adiabaticHeadlineLine = adiabaticWaterScreening
    ? ` Adiabatic assist, if used: ${formatWaterRate(adiabaticWaterScreening.averageDailyMakeupGallons)} makeup on a TMY annual-average day.`
    : '';
  const waterStressClause = `the WRI Aqueduct screen rates this basin High baseline water stress${waterRiskScore != null ? ` (overall water-risk ${formatDecimal(waterRiskScore, 2)}/5)` : ''}`;
  const coolingWaterPosition = wetDemandLine == null
    ? null
    : wetScreenedOut
      ? `Wet cooling is screened out for this site: ${waterStressClause}, so a wet tower's ${wetDemandLine} is unlikely to be supplied or permitted here. Cooling-process water then depends on verified dry capacity.${adiabaticHeadlineLine}`
      : wetCoolingCase === 'alternative'
        ? `Recorded cold end: ${(heatRejection ?? 'unknown').replaceAll('-', ' ')}. Cooling-process water is conditional on the vendor proving full-duty capacity at the site design temperature; the wet-tower case (${wetDemandLine}) is kept as an alternative-technology sensitivity, not the design.${adiabaticHeadlineLine}`
        : wetWithdrawalLine == null
          ? null
          : [
              `Water, if wet cooling: ${wetWithdrawalLine}`,
              heatRejection === 'unknown'
                ? ' — the heat-rejection design is not yet recorded, so this is the wet-cooling alternative, not a chosen case.'
                : '.',
              ' Modelled screening at the cooling-process boundary, not a permitted or requested withdrawal.',
            ].join('');
  const coolingWaterDetails: CallReadyAnswerDetail[] = coolingWaterPosition
    ? [{ label: 'Cooling-process water', value: coolingWaterPosition }]
    : [];
  const waterScreeningBasisDetails: CallReadyAnswerDetail[] = [
    ...(hasScreenedWater
      ? [{
          label: 'Water screening basis',
          value: [
            waterCalculationBasis
              ? `Screening preview using ${waterCalculationBasis}.`
              : 'Screening preview using the current project inputs.',
            heatRejection === 'unknown'
              ? wetScreenedOut
                ? 'The heat-rejection design is not recorded; the wet-tower case is kept as a screened-out case.'
                : 'The heat-rejection design is not recorded, so the cooling rows are alternatives.'
              : 'The recorded cooling case leads the rows.',
            adiabaticWaterScreening
              ? 'Site-coordinate-selected, nearest-grid hourly TMY estimates the adiabatic-assist water; vendor full-duty capacity remains unverified.'
              : dryCoolingDesignScreen
                ? 'The dry/adiabatic temperature check uses current SED climate; recalculate to add the hourly assist-water estimate. Vendor capacity remains unverified.'
                : waterScreen?.resolvedContext?.ambientConditions
                  ? 'Current SED climate and the recorded cooling context qualify the screen.'
                  : 'Local climate was not available for a site-specific adjustment.',
            dutyBasisNote,
            'Broad total-site benchmarks are secondary context. Values are screening bands, not permitted or measured demand.',
          ].filter((part): part is string => part != null).join(' '),
        }]
      : hasModelledWater
        ? []
        : [{
            label: 'Water-use quantity',
            value: waterEvidence[0]?.source.finding
              ? `The latest water analysis identifies ${waterEvidence[0].source.finding}, but no structured water quantity is available.`
              : `No modeled water-use result is available${waterScreeningReason ? ` — water-use screening failed: ${waterScreeningReason}` : '.'}`,
          }]),
  ];

  const visualPowerRange = economicLoadMw != null
    ? { min: economicLoadMw, max: economicLoadMw }
    : economicLoadRange != null
      ? economicLoadRange
      : recordedPowerRange;
  const powerVisualIsExact = visualPowerRange != null
    && visualPowerRange.min === visualPowerRange.max;
  const powerVisualRows: CallReadyRangeVisualRow[] = visualPowerRange == null
    ? []
    : [{
        id: 'declared-power',
        label: economicLoadMw != null
          ? `Economic screening ${powerBasisLabel.toLowerCase()}`
          : economicLoadRange != null
            ? 'Economic screening range'
            : power.isExact
              ? powerBasisLabel === 'IT load' ? 'Declared IT load' : 'Declared facility demand'
              : 'Recorded planning range',
        low: visualPowerRange.min,
        high: visualPowerRange.max,
        valueLabel: powerDisplayLabel,
      }];
  const dutyWaterVisualRows: CallReadyRangeVisualRow[] = hasModelledWater
    ? [
        ...(modelledWater?.totalConsumptionGalPerDay != null ? [{
          id: 'modeled-consumption',
          label: 'Peak consumption',
          low: modelledWater.totalConsumptionGalPerDay,
          high: modelledWater.totalConsumptionGalPerDay,
          valueLabel: formatWhole(modelledWater.totalConsumptionGalPerDay, 'gal/day'),
        }] : []),
        ...(modelledWater?.totalWithdrawalGalPerDay != null ? [{
          id: 'modeled-withdrawal',
          label: 'Peak withdrawal',
          low: modelledWater.totalWithdrawalGalPerDay,
          high: modelledWater.totalWithdrawalGalPerDay,
          valueLabel: formatWhole(modelledWater.totalWithdrawalGalPerDay, 'gal/day'),
        }] : []),
      ]
    : [];
  const benchmarkWaterVisualRows: CallReadyRangeVisualRow[] = [
        ...(modernWaterScenario ? [{
          id: 'modern-operator',
          label: modernWaterScenario.flowBasis === 'unspecified'
            ? 'Total site · modern operator · flow basis not specified'
            : 'Total site · modern operator',
          low: modernWaterScenario.averageGallonsPerDay.low,
          high: modernWaterScenario.averageGallonsPerDay.high,
          valueLabel: formatWaterRate(modernWaterScenario.averageGallonsPerDay),
        }] : []),
        ...(nationalWaterScenario ? [{
          id: 'national-current-average',
          label: 'Total site · LBNL 2023 national average',
          low: nationalWaterScenario.averageGallonsPerDay.low,
          high: nationalWaterScenario.averageGallonsPerDay.high,
          valueLabel: formatWaterRate(nationalWaterScenario.averageGallonsPerDay),
        }] : []),
      ];
  // Rows that lead the cooling chart follow the recorded design: the wet
  // rows are drawn as the selected case, or hollow while the cold end is still
  // open. A ruled-out or alternative wet case leaves the leading chart
  // entirely (see `wetSideVisualRows`), and 100%-load rows are sensitivities
  // that never lead.
  const wetRowEmphasis: CallReadyVisualEmphasis = wetCoolingCase === 'selected' || wetCoolingCase === 'hybrid'
    ? 'selected'
    : 'alternative';
  const adiabaticVisualRows: CallReadyRangeVisualRow[] = adiabaticWaterScreening ? [{
    id: 'adiabatic-assist-makeup',
    label: 'Adiabatic assist makeup · TMY annual average',
    low: adiabaticWaterScreening.averageDailyMakeupGallons.low,
    high: adiabaticWaterScreening.averageDailyMakeupGallons.high,
    valueLabel: formatWaterRate(adiabaticWaterScreening.averageDailyMakeupGallons),
    emphasis: 'selected',
  }, {
    id: 'adiabatic-assist-peak-day',
    label: 'Adiabatic assist makeup · TMY peak day',
    low: adiabaticWaterScreening.peakDailyMakeupGallons.low,
    high: adiabaticWaterScreening.peakDailyMakeupGallons.high,
    valueLabel: formatWaterRate(adiabaticWaterScreening.peakDailyMakeupGallons),
    emphasis: 'selected',
  }] : [];
  const wetAverageVisualRows: CallReadyRangeVisualRow[] = [
    ...(wetConsumptionScenario ? [{
      id: 'wet-consumption',
      label: wetConsumptionScenario.applicability === 'selected-technology'
        ? 'Wet tower consumption · selected'
        : hybridHeatRejection
          ? 'Hybrid wet mode consumption'
          : 'Wet tower consumption',
      low: wetConsumptionScenario.averageGallonsPerDay.low,
      high: wetConsumptionScenario.averageGallonsPerDay.high,
      valueLabel: formatWaterRate(wetConsumptionScenario.averageGallonsPerDay),
      emphasis: wetRowEmphasis,
    }] : []),
    ...(wetWithdrawalScenario ? [{
      id: 'wet-withdrawal',
      label: wetWithdrawalScenario.applicability === 'selected-technology'
        ? 'Wet tower withdrawal · selected'
        : hybridHeatRejection
          ? 'Hybrid wet mode withdrawal'
          : 'Wet tower withdrawal',
      low: wetWithdrawalScenario.averageGallonsPerDay.low,
      high: wetWithdrawalScenario.averageGallonsPerDay.high,
      valueLabel: formatWaterRate(wetWithdrawalScenario.averageGallonsPerDay),
      emphasis: wetRowEmphasis,
    }] : []),
  ];
  const wetFullLoadVisualRows: CallReadyRangeVisualRow[] = [
    ...(wetConsumptionScenario?.fullLoadGallonsPerDay ? [{
      id: 'wet-consumption-full-load',
      label: hybridHeatRejection
        ? 'Hybrid wet mode consumption · 100% load'
        : 'Wet tower consumption · 100% load',
      low: wetConsumptionScenario.fullLoadGallonsPerDay.low,
      high: wetConsumptionScenario.fullLoadGallonsPerDay.high,
      valueLabel: formatWaterRate(wetConsumptionScenario.fullLoadGallonsPerDay),
      emphasis: 'benchmark' as const,
    }] : []),
    ...(wetWithdrawalScenario?.fullLoadGallonsPerDay ? [{
      id: 'wet-withdrawal-full-load',
      label: hybridHeatRejection
        ? 'Hybrid wet mode withdrawal · 100% load'
        : 'Wet tower withdrawal · 100% load',
      low: wetWithdrawalScenario.fullLoadGallonsPerDay.low,
      high: wetWithdrawalScenario.fullLoadGallonsPerDay.high,
      valueLabel: formatWaterRate(wetWithdrawalScenario.fullLoadGallonsPerDay),
      emphasis: 'benchmark' as const,
    }] : []),
  ];
  const coolingSystemWaterVisualRows: CallReadyRangeVisualRow[] = [
    ...adiabaticVisualRows,
    ...(wetRowsLeadPrimary ? wetAverageVisualRows : []),
  ];
  const wetSideVisualRows: CallReadyRangeVisualRow[] = wetRowsLeadPrimary
    ? wetFullLoadVisualRows
    : [...wetAverageVisualRows, ...wetFullLoadVisualRows];
  const impactConcernDetails: CallReadyAnswerDetail[] = promotedImpact ? [
    {
      label: 'Modeled on-site generation residual CO₂',
      value: `${formatWhole(promotedImpact.result.emissions.residualCo2TonnesPerYear, 'tonnes/year')} · purchased-grid electricity and unmodeled standby excluded`,
    },
    ...(promotedImpact.result.emissions.noxTonnesPerYear != null
      ? [{ label: 'Modeled NOx', value: formatWhole(promotedImpact.result.emissions.noxTonnesPerYear, 'tonnes/year') }]
      : []),
    {
      label: 'Modeled receptor noise screen',
      value: `${promotedImpact.result.noise.length} ${promotedImpact.result.noise.length === 1 ? 'receptor' : 'receptors'} screened · ${promotedImpact.result.noise.filter((result) => result.verdict === 'fail').length} ${promotedImpact.result.noise.filter((result) => result.verdict === 'fail').length === 1 ? 'exceedance' : 'exceedances'}`,
    },
  ] : [];
  let communityUnavailableCause: string | undefined = undefined;
  if (environmentalDetailResult.status === 'failed') {
    communityUnavailableCause = 'public environmental screen failed on regrid parcels';
  } else if (floodSection === null && currentSed?.flood?.status === 'unavailable') {
    communityUnavailableCause = 'flood screen failed on the FEMA server';
  } else if (waterStressSection === null && currentSed?.waterStress?.status === 'unavailable') {
    communityUnavailableCause = 'water-stress screen failed on the WRI server';
  }

  // ── Power & cooling: the lead answer and its risks ─────────────────────
  // Gateway Power delivers on-site (BTM) power and cooling for AI data
  // centers, so the brief leads with the site's BTM architecture, power type,
  // DC load, rack cooling and cold end — each recorded, recommended by the
  // platform, or not yet determined — then the evidenced risks with actions.
  const usableReport = (type: ReportTypeId): SiteRagReport | null => (
    isReportUsable(type) ? latestReports.get(type) ?? null : null
  );
  let studyAreaAcres: number | null = null;
  try {
    studyAreaAcres = resolveSiteStudyArea(site)?.scope.grossAreaAcres ?? null;
  } catch {
    // An invalid study-area selection reads as "no study area"; the Map tab owns the repair.
    studyAreaAcres = null;
  }
  const seismicSection = trustedSedSection(currentSed?.seismic);
  const exactScreeningSource = options.screeningRecommendationBasis === undefined
    ? null : resolveCurrentScreeningRecommendationReport(canonicalSite, options.screeningRecommendationBasis);
  const screeningAssessment = options.screeningRecommendationBasis === undefined
    ? usableReport('feasibility')
    : exactScreeningSource ? presentReportsText([exactScreeningSource])[0] ?? null : null;
  // Project the same independent-evidence checks into the availability
  // resolver. Preserve the stored SED and keep missing evidence non-vetoing.
  const availabilitySed = currentSed ? {
    ...currentSed,
    ...(!gridSection && currentSed.grid ? { grid: { ...currentSed.grid, data: null } } : {}),
    ...(!isTrustedSedSection(currentSed.onSiteInfrastructure) && currentSed.onSiteInfrastructure
      ? { onSiteInfrastructure: { ...currentSed.onSiteInfrastructure, data: null } } : {}),
    ...(!trustedSedSection(currentSed.fuelSupply) && currentSed.fuelSupply
      ? { fuelSupply: { ...currentSed.fuelSupply, data: null } } : {}),
  } : null;
  // A promoted scenario is a RECORDED choice on these lines, so it must be the
  // one Project Intent actually promoted (id and hash stamps), not merely a
  // snapshot left on the site.
  const promotedPowerScenario = promotedImpact
    ? resolvePromotedGenerationScenario(site.projectIntent, promotedImpact, null, site.generationAssessmentSelection, site.generationProjectInputBinding)
    : null;
  const powerCooling = buildPowerCoolingAnswers({
    site,
    sed: currentSed,
    feasibility: screeningAssessment,
    environmental: usableReport('environmental'),
    promotedScenario: promotedPowerScenario,
    stage: stage.briefStage,
    supplyChain: usableReport('supply-chain-risk'),
    testFit: usableReport('test-fit'),
    availability: resolvePerSourceAvailability({ sed: availabilitySed, promotedScenario: promotedImpact ?? null }),
    studyAreaAcres,
    asOf: options.asOf ?? new Date().toISOString().slice(0, 10),
    trusted: {
      waterStress: waterStressSection,
      flood: floodSection,
      seismic: seismicSection,
      grid: gridSection,
      interconnection: interconnectionSection,
    },
    dryCapacity: dryCoolingDesignScreen
      ? {
          screened: true,
          dryOnlyCandidate: hasDryOnlyScreeningCandidate,
          peakDryBulbProxyC: dryCoolingDesignScreen.sitePeakDryBulbProxyC,
          ...(w45DryCheck ? { w45Status: dryCoolingCapacityStatusLabel(w45DryCheck.dryStatus) } : {}),
        }
      : null,
  });
  const sedSectionLabel: Readonly<Record<PowerCoolingSedSection, string>> = {
    waterStress: 'Water stress',
    grid: 'Grid infrastructure',
    fuelSupply: 'Fuel supply',
    onSiteInfrastructure: 'Site-deliverable fuel infrastructure',
    environmental: 'Environmental screening',
    flood: 'Flood screening',
    seismic: 'Seismic',
    interconnection: 'Interconnection',
  };
  const dryScreenSourceIds = new Set((dryCoolingScenario?.sourceRefs ?? []).map(source => `water-source:${source.id}`));
  const powerCoolingEvidence = (refs: readonly PowerCoolingEvidenceRef[]): CallReadyEvidenceSource[] => {
    const sources: CallReadyEvidenceSource[] = [];
    for (const ref of refs) {
      switch (ref.kind) {
        case 'site':
          sources.push(siteSource);
          break;
        case 'scenario':
          if (impactSource) sources.push(impactSource);
          break;
        case 'sed': {
          const section = currentSed?.[ref.section];
          if (currentSed && section?.data != null && isTrustedSedSection(section)) {
            sources.push(sedEvidenceSource<unknown>(currentSed, ref.section, sedSectionLabel[ref.section], { ...section, data: section.data }));
          }
          break;
        }
        case 'report': {
          const report = exactScreeningSource?.id === ref.reportId
            ? exactScreeningSource : analyses.find(candidate => candidate.id === ref.reportId);
          if (report) {
            const type = reportTypeOf(report);
            sources.push({
              id: `analysis:${report.id}`,
              kind: 'analysis',
              label: REPORT_TYPE_REGISTRY[type].shortLabel,
              reportId: report.id,
              reportType: type,
              vintage: report.updatedAt ?? report.createdAt,
            });
          }
          break;
        }
        case 'stage2-source': {
          const entry = STAGE2_SOURCES[ref.sourceId];
          sources.push({
            id: `stage2-source:${entry.id}`,
            kind: entry.url ? 'public-record' : 'model-result',
            label: `${entry.publisher} · ${entry.title}`,
            finding: entry.finding,
            ...(entry.url ? { url: entry.url } : {}),
          });
          break;
        }
        case 'water-screen':
          sources.push(...waterScreenSources.filter(source => dryScreenSourceIds.has(source.id)));
          break;
      }
    }
    return sources.filter((source, index) => sources.findIndex(candidate => candidate.id === source.id) === index);
  };
  // The general Brief evidence path can retain a readable historical report
  // during refresh. Current recommendation claims require the host-bound
  // source above; an absent binding must never fall back to that report.
  const currentRecommendationReport = exactScreeningSource
    ? presentReportsText([exactScreeningSource])[0] ?? null : null;
  const recommendationBasisDiagnostic = options.recommendationFirst && site.projectIntent?.screeningMode === 'recommend'
    ? diagnoseCurrentScreeningRecommendationBasis(canonicalSite, options.screeningRecommendationBasis, latestReports.get('feasibility') ?? null)
    : null;
  const currentRecommendationSet = currentRecommendationReport
    ? derivePresentedScreeningRecommendationSetV1(currentRecommendationReport, site.id)
    : null;
  const recommendationView = options.recommendationFirst && site.projectIntent?.screeningMode === 'recommend'
    ? recommendationFirstBrief(
        powerCooling.lines,
        deriveScreeningRecommendationsForPresentation(currentRecommendationReport, site.id),
        {
          recommendationSet: currentRecommendationSet,
          sourceDiagnostic: recommendationBasisDiagnostic,
        },
      )
    : null;
  const toAnswerLine = (line: PowerCoolingLine): CallReadyAnswerLine => ({
    id: line.id,
    label: line.label,
    value: line.value,
    basis: line.basis,
    basisNote: line.basisNote,
    ...(line.openIn ? { openIn: line.openIn } : {}),
    ...(line.nextStep ? { nextStep: line.nextStep } : {}),
    ...(line.route ? { route: line.route } : {}),
    ...(line.spokenLabel ? { spokenLabel: line.spokenLabel } : {}),
    ...(line.settlesWith ? { settlesWith: line.settlesWith } : {}),
    ...(line.rationale ? { rationale: line.rationale } : {}),
    ...(recommendationView?.recordedInputs.has(line.id) ? { recordedInput: {
      value: recommendationView.recordedInputs.get(line.id)!.value,
      basisNote: recommendationView.recordedInputs.get(line.id)!.basisNote,
      ...(recommendationView.recordedInputs.get(line.id)!.nextStep ? { nextStep: recommendationView.recordedInputs.get(line.id)!.nextStep } : {}),
    } } : {}),
  });
  const powerCoolingLines = recommendationView?.lines ?? powerCooling.lines;
  const sourceResourceDetails = siteResourceDetails(powerCooling.resourceStatements);
  // Status, next step, presenter guidance and the composed spoken summary
  // follow the Stage 1 gap rule: a Stage 2 decision field left open (rack
  // loop, cold end, technology) is not a screening gap. Until the brief
  // carries the decision stage, a usable Screening Assessment stands in for
  // "early screening is complete" when choosing the next step.
  const framingOptions = {
    screeningAssessmentUsable: screeningAssessment != null,
    screeningComplete: stage.screeningComplete,
    stage: stage.briefStage,
  };
  const powerCoolingFraming = framePowerCoolingAnswer(powerCoolingLines, framingOptions);
  if (recommendationView) {
    // A retained demand is an input to compare; do not falsely announce that
    // no load is recorded merely because no recommended size range exists.
    const recordedDemand = recommendationView.recordedInputs.get('dc-power');
    const spokenLines = powerCoolingLines.map(line => line.id === 'dc-power' && line.basis === 'not-determined' && recordedDemand ? recordedDemand : line);
    powerCoolingFraming.summary = powerCoolingLines.some(line => line.basis === 'recommended')
      ? framePowerCoolingAnswer(spokenLines, framingOptions).summary
      : 'No current saved screening recommendation is available for the power and cooling approach. Recorded inputs remain visible for comparison; they do not establish a recommended design. Review or refresh Screening Assessment.';
  }
  const recordedPowerCoolingDetails: CallReadyAnswerDetail[] = recommendationView
    ? [...recommendationView.recordedInputs.values()].map(line => ({ label: `Recorded input · ${line.label}`,
      value: [line.value, line.basisNote, line.nextStep].filter(Boolean).join('. ') }))
    : [];
  const powerCoolingRationaleDetails: CallReadyAnswerDetail[] = powerCoolingLines
    .filter(line => line.basis === 'recommended' && line.rationale)
    .map(line => ({ label: `${line.label} · why recommended`, value: line.rationale! }));
  // Economic screening inputs are a what-if for the water and jobs screens,
  // never the recorded design: when they change the load or the cooling, the
  // Details say so instead of the lines adopting them.
  const intentBasisLabel = site.projectIntent?.powerBasis === 'it-load'
    ? 'IT load'
    : site.projectIntent?.powerBasis === 'facility' ? 'Facility demand' : 'Basis not yet recorded';
  const screeningLoadRange = economicLoadMw != null
    ? { min: economicLoadMw, max: economicLoadMw }
    : economicLoadRange;
  const recordedLoadRange = power.bucket !== 'undecided' ? recordedPowerRange : null;
  const screeningLoadDiffers = screeningLoadRange != null
    && !(recordedLoadRange != null
      && recordedLoadRange.min === screeningLoadRange.min
      && recordedLoadRange.max === screeningLoadRange.max);
  const screeningBasisDiffers = options.economicContributionEnabled === true
    && site.economicContributionInputs?.loadBasis != null
    && powerBasisLabel !== intentBasisLabel;
  const whatIfWater = options.economicContributionEnabled ? site.economicContributionInputs?.waterUse : undefined;
  const whatIfRackCooling = whatIfWater?.rackCoolingStrategy
    && whatIfWater.rackCoolingStrategy !== 'undecided'
    && whatIfWater.rackCoolingStrategy !== site.projectIntent?.coolingStrategy
    ? spokenCoolingStrategy(whatIfWater.rackCoolingStrategy)
    : null;
  const whatIfHeatRejection = whatIfWater?.heatRejection
    && whatIfWater.heatRejection !== 'unknown'
    && whatIfWater.heatRejection !== site.projectIntent?.bopHeatRejection
    ? spokenHeatRejection(whatIfWater.heatRejection)
    : null;
  const screeningWhatIfParts = [
    screeningLoadDiffers || screeningBasisDiffers
      ? `${powerDisplayLabel}${powerBasisLabel === 'Basis not yet recorded' ? '' : ` ${lowerFirst(powerBasisLabel)}`}`
      : null,
    whatIfRackCooling,
    whatIfHeatRejection,
  ].filter((part): part is string => part != null);
  const screeningWhatIfDetails: CallReadyAnswerDetail[] = screeningWhatIfParts.length > 0
    ? [{
        label: 'Screening what-if',
        value: `Screening inputs use ${joinList(screeningWhatIfParts)} as a what-if, not the recorded design.`,
      }]
    : [];
  const powerCoolingBases = uniqueBases([
    ...powerCooling.lineBases,
    ...(recommendationView?.lines.some(line => line.basis === 'recommended') ? ['report-finding' as const] : []),
    ...(hasWaterQuantity ? ['modelled-screening' as const] : []),
    ...(waterEvidence.length > 0 ? ['report-finding' as const] : []),
  ]);
  const powerCoolingEvidenceSources = [
    ...powerCoolingEvidence(powerCoolingLines.flatMap(line => line.evidenceRefs)),
    siteSource,
    ...(impactSource ? [impactSource] : []),
    ...waterClimateEvidence,
    ...waterScreenSources,
    ...adiabaticWaterSources,
    ...waterEvidence.map(({ source }) => source),
  ].filter((source, index, all) => all.findIndex(candidate => candidate.id === source.id) === index);
  // Report prompts keep the water-facet framing this question had as "Power
  // and water": the prompt's synthetic site carries no grid vs on-site
  // strategy, so framing the lines would tell every report to choose one even
  // where it is recorded. Byte-identical to the pre-lead framing.
  const waterScreenFraming = (): CallReadyPromptFraming => {
    const resourceBases = uniqueBases([
      ...(hasPower ? ['site-fact' as const] : []),
      ...(hasWaterQuantity ? ['modelled-screening' as const] : []),
      ...(waterEvidence.length > 0 ? ['report-finding' as const] : []),
    ]);
    const nextInput = !hasPower || !hasWaterQuantity
      ? 'Use the Early Screening Assessment to recommend a conditional DC size range and power/cooling paths from usable land, water and fuel evidence. Quantified water duty requires a stated load range and its IT or facility basis.'
      : wetScreenedOut
        ? 'Confirm a dry or hybrid cold-end design; wet-tower water is screened out by High basin water stress. Use the Early Screening Assessment to confirm supply and permitting.'
      : waterScreen?.resolvedContext?.heatRejection === 'unknown'
        ? 'Confirm the cold-end heat-rejection design to narrow the screening range; use the Early Screening Assessment to confirm supply and permitting.'
        : hybridHeatRejection
          ? adiabaticWaterScreening
            ? 'Confirm the equipment selection and control sequence, then record the expected wet-tower operating fraction for a final blended design case. Use the Early Screening Assessment to confirm supply and permitting.'
            : 'Recalculate for the site-coordinate-selected hourly adiabatic estimate, then record the expected wet-mode operating fraction for a blended design case. Use the Early Screening Assessment to confirm supply and permitting.'
          : (heatRejection === 'dry-cooler' || heatRejection === 'free-cooling')
            ? 'Verify dry-cooler capacity at the site design dry bulb using the required facility-water temperatures, approach, equipment curve, and redundancy margin; add the adiabatic control schedule if assist is required.'
          : heatRejection === 'once-through'
            ? 'Provide sourced withdrawal and return-flow factors before quantifying once-through cooling; use the Early Screening Assessment to confirm supply and permitting.'
            : undefined;
    return {
      shortLabel: 'Power and water',
      status: statusFromSignals(Number(hasPower) + Number(hasWaterQuantity), 2),
      bases: resourceBases.length > 0 ? resourceBases : ['unavailable'],
      ...(nextInput ? { nextInput } : {}),
    };
  };

  const evidencedRisks = powerCooling.risks.filter(risk => risk.state === 'evidenced');
  const unassessedRisks = powerCooling.risks.filter(risk => risk.state === 'not-assessed');
  const assessedRiskTopicCount = evidencedRisks.length + powerCooling.clearTopics.length;
  const riskList = joinList;
  const leadRisk = evidencedRisks[0];
  const risksRecommendation: CallReadyRecommendation = leadRisk
    ? recommend('caution', `Start with ${lowerFirst(leadRisk.title)}: ${lowerFirst(leadRisk.action)}${unassessedRisks.length > 0 ? ' Say plainly which topics are not yet assessed; they are open, not clear.' : ''}`)
    : unassessedRisks.length > 0
      ? recommend('input', `Assess ${riskList(unassessedRisks.map(risk => lowerFirst(risk.title)))} before presenting a risk position; no evidenced risk is not the same as no risk.`)
      : recommend('proceed', 'No power or cooling risk is evidenced in the current screens; present them as screening results, not permits or utility commitments.');
  const risksSummary = [
    evidencedRisks.length > 0
      ? `${evidencedRisks.length} evidenced ${evidencedRisks.length === 1 ? 'risk' : 'risks'}: ${evidencedRisks.map(risk => risk.title).join('; ')}.`
      : 'No power or cooling risk is evidenced in the current record.',
    unassessedRisks.length > 0
      ? `Not yet assessed: ${riskList(unassessedRisks.map(risk => lowerFirst(risk.title)))}.`
      : null,
    powerCooling.clearTopics.length > 0
      ? `Screened with no evidenced risk: ${riskList(powerCooling.clearTopics)}.`
      : null,
  ].filter((part): part is string => part != null).join(' ');

  const infrastructureOpenItems = [
    !infrastructurePowerCovered ? 'the project power basis' : null,
    !infrastructureGridCovered && !isIslandedArchitecture ? 'grid interconnection' : null,
    !infrastructureConnectivityCovered ? 'carrier capacity and route diversity' : null,
    !infrastructureLayoutCovered ? 'the buildable layout' : null,
  ].filter((item): item is string => item != null);
  const infrastructureRecommendation: CallReadyRecommendation = infrastructureOpenItems.length === 0
    ? recommend(
        'proceed',
        isIslandedArchitecture
          ? 'Present the known infrastructure positions as the baseline; site is islanded off-grid so no utility interconnection study is required. Focus diligence on on-site generation reliability and fuel supply.'
          : 'Present the four known infrastructure positions as the baseline and ask the utility for the interconnection study timeline; nothing here is a firm service commitment yet.',
      )
    : recommend('input', `Confirm ${infrastructureOpenItems.length === 1 ? infrastructureOpenItems[0] : `${infrastructureOpenItems.slice(0, -1).join(', ')} and ${infrastructureOpenItems.at(-1)}`} before quoting a schedule${infrastructureSignalCount > 0 ? `; lead with the ${infrastructureSignalCount} of 4 items already known` : ''}.`);
  const communityRecommendation: CallReadyRecommendation = communityScreeningReady
    ? recommend('proceed', `Present the completed screens as an initial issue screen${communityUnresolvedTopics.length > 0 ? ` and say plainly that ${communityUnresolvedLabel} ${communityUnresolvedTopics.length === 1 ? 'remains' : 'remain'} open` : ''}; do not state that impacts are permitted, mitigated, or publicly accepted.`)
    : communityEvidenceTopicCount > 0 || communitySignalCount > 0
      ? recommend('input', `Close ${communityUnresolvedLabel || 'the open topics'} before external use; until then lead with the completed screens and name what is still open.`)
      : recommend('input', 'Do not characterise community impacts yet; add noise and emissions, environmental, traffic and visual, and public-sentiment evidence first.');

  const questions: CallReadyAnswer[] = [
    {
      id: 'resources',
      ordinal: 1,
      shortLabel: 'Power & cooling',
      question: 'What power, cooling and BTM architecture is recommended for this site?',
      status: powerCoolingFraming.status,
      bases: powerCoolingBases.length > 0 ? powerCoolingBases : ['unavailable'],
      summary: powerCoolingFraming.summary,
      lines: powerCoolingLines.map(toAnswerLine),
      // The quantities (MW envelope, water bands, screened-out wet tower,
      // climate basis) support the lines; they sit in collapsed Details and
      // visuals, never in the lead. The cooling-process water sentence stays
      // the first row (it is still read out on AHJ calls), then any screening
      // what-if, the reasons behind recommended lines, and the screen's basis.
      details: [
        ...coolingWaterDetails,
        ...recordedPowerCoolingDetails,
        ...screeningWhatIfDetails,
        ...powerCoolingRationaleDetails,
        ...sourceResourceDetails,
        ...waterScreeningBasisDetails,
        ...waterDetails,
      ],
      evidence: powerCoolingEvidenceSources,
      supportingVisualsLabel: 'Power and water screening quantities',
      visuals: [
        ...(powerVisualRows.length > 0 ? [{
          id: 'power-envelope',
          title: 'Power envelope',
          unitLabel: 'MW',
          rows: powerVisualRows,
          note: powerVisualIsExact
            ? 'Recorded project value.'
            : 'Planning range; endpoint analyses preserve the low and high cases.',
        }] : []),
        ...(coolingSystemWaterVisualRows.length > 0 || (dryCoolingScenario && wetCoolingCase !== 'none' && !wetRowsLeadPrimary) ? [{
          id: 'cooling-water-envelope',
          title: wetCoolingCase === 'selected'
            ? 'Selected wet-cooling case'
            : wetCoolingCase === 'hybrid'
              ? 'Hybrid cooling operating modes'
              : wetRowsLeadPrimary
                ? 'Cooling-design sensitivity'
                : wetScreenedOut
                  ? 'Cooling design · wet tower screened out'
                  : 'Recorded cooling design',
          unitLabel: 'gal/day',
          rows: coolingSystemWaterVisualRows,
          callouts: dryCoolingScenario ? [{
            id: 'conditional-dry-mode',
            label: hybridHeatRejection ? 'Dry mode' : 'Dry-cooling mode',
            value: hasDryOnlyScreeningCandidate
              ? '0 cooling-process gal/day · conditional'
              : 'Full-duty dry capacity not established',
            note: `${w45DryCheck
              ? `The published-temperature W45 screen is ${dryCoolingCapacityStatusLabel(w45DryCheck.dryStatus)}.`
              : 'A site-temperature screen is available, but full-load equipment capacity is not proven.'} ${hasDryOnlyScreeningCandidate ? 'Zero applies only to cooling-process water during verified dry operation. ' : 'No numeric zero is shown for this site condition. '}Vendor curves must show the selected dry-cooler/CDU/heat-exchanger train can reject the full facility duty at the site design temperature. This is not zero total-site water.`,
            tone: 'caution' as const,
          }, ...(hybridHeatRejection && !adiabaticWaterScreening ? [{
            id: 'adiabatic-assist-mode',
            label: 'Adiabatic assist',
            value: adiabaticClimateUnavailable ? 'Hourly weather unavailable' : 'Recalculate for a climate estimate',
            note: adiabaticClimateUnavailable
              ? 'The last site-coordinate TMY lookup failed. Retry the screening calculation; the platform will not substitute monthly climate for an annual estimate.'
              : 'The refreshed screen loads site-coordinate-selected, nearest-grid hourly TMY weather and applies disclosed industry airflow, pad-effectiveness, activation, and water-quality assumptions. Equipment selection then narrows the result.',
            tone: 'neutral' as const,
          }] : [])] : undefined,
          note: `Calculated average-day endpoints${waterCalculationBasis ? ` · ${waterCalculationBasis}` : ''}. ${adiabaticWaterScreening ? `The adiabatic rows use a ${adiabaticWaterScreening.provenance.tmy.hourCount.toLocaleString('en-US')}-hour site-coordinate-selected, nearest-grid TMY; the TMY peak day is a typical-weather-year screening result, not an extreme design-weather or permitted maximum-day value. ` : ''}${wetCoolingCase === 'selected'
            ? 'The wet rows reflect the recorded heat-rejection choice.'
            : wetCoolingCase === 'hybrid'
              ? 'The wet rows are the wet-mode endpoint; a blended result still requires the wet-mode operating fraction.'
              : wetCoolingCase === 'open'
                ? 'The wet rows are an alternative technology case, drawn hollow until the cold end is recorded.'
                : 'Only the recorded design is plotted; the wet-tower case is kept below.'}`,
        }] : []),
        ...(wetSideVisualRows.length > 0 ? [{
          id: wetRowsLeadPrimary
            ? 'cooling-full-load-sensitivity'
            : wetScreenedOut
              ? 'cooling-wet-screened-out'
              : 'cooling-wet-alternative',
          title: wetRowsLeadPrimary
            ? 'Wet-tower rows at 100% load'
            : wetScreenedOut
              ? 'Wet cooling · screened out for this site'
              : 'Wet-tower alternative · not the recorded design',
          unitLabel: 'gal/day',
          rows: wetSideVisualRows,
          note: wetRowsLeadPrimary
            ? 'Technology sensitivity at 100% load; not a permitted or design-day peak.'
            : 'Full-evaporative endpoints at average and 100% load, kept for the record; not a permitted or design-day peak.',
          presentation: wetScreenedOut ? 'screened-out' as const : 'context' as const,
          ...(wetScreenedOut && wetDemandLine
            ? { reason: `${waterStressClause.charAt(0).toUpperCase()}${waterStressClause.slice(1)}; a wet tower would need ${wetDemandLine}, which is unlikely to be supplied or permitted here. The figures stay on the record as a sensitivity only.` }
            : {}),
        }] : []),
        ...(dutyWaterVisualRows.length > 0 ? [{
          id: 'water-use-envelope',
          title: 'Calculated total-site water · scenario duty (equipment/process boundary)',
          unitLabel: 'gal/day',
          rows: dutyWaterVisualRows,
          note: 'Promoted peak scenario duty. A different quantity from the site-boundary benchmark bands below; the two are not reconciled.',
          presentation: 'primary' as const,
        }] : []),
        ...(benchmarkWaterVisualRows.length > 0 ? [{
          id: 'water-benchmark-context',
          title: 'Total-site benchmark range',
          unitLabel: 'gal/day',
          rows: benchmarkWaterVisualRows,
          note: `Calculated from ${waterCalculationBasis ?? 'the current screening inputs'} using alternative published WUE benchmarks. These are not additive, a probability interval, the selected cooling design, permitted demand, or measured use.${modernWaterScenario?.flowBasis === 'unspecified' ? ' The modern-operator source does not specify whether its water metric is consumption or withdrawal, so do not compare it directly with the LBNL consumption row.' : ''}`,
          presentation: 'context' as const,
        }] : []),
      ],
      ...(powerCoolingFraming.recommendation ? { recommendation: powerCoolingFraming.recommendation } : {}),
      ...(powerCoolingFraming.nextInput ? { nextInput: powerCoolingFraming.nextInput } : {}),
      ...(powerCoolingFraming.route ? { route: powerCoolingFraming.route } : {}),
      promptFraming: waterScreenFraming(),
    },
    {
      id: 'power-cooling-risks',
      ordinal: 2,
      shortLabel: 'Risks & actions',
      question: 'What are the key power and cooling risks, and what should happen next?',
      status: unassessedRisks.length === 0 && assessedRiskTopicCount > 0
        ? 'ready'
        : assessedRiskTopicCount > 0 ? 'partial' : 'unavailable',
      bases: powerCooling.riskBases.length > 0 ? uniqueBases(powerCooling.riskBases) : ['unavailable'],
      summary: risksSummary,
      risks: powerCooling.risks.map(risk => ({
        id: risk.id,
        area: RISK_AREA_LABEL[risk.area],
        title: risk.title,
        state: risk.state,
        severity: risk.severity,
        finding: risk.finding,
        source: risk.source,
        action: risk.action,
        origin: risk.origin ?? 'early-screening',
        route: risk.route,
        spokenResponse: risk.spokenResponse,
      })),
      details: powerCooling.clearTopics.length > 0
        ? [{ label: 'Screened with no evidenced risk', value: powerCooling.clearTopics.join(' · ') }]
        : [],
      evidence: powerCoolingEvidence(evidencedRisks.flatMap(risk => risk.evidenceRefs)),
      recommendation: risksRecommendation,
      ...(unassessedRisks[0] ? { nextInput: unassessedRisks[0].action } : {}),
    },
    {
      id: 'infrastructure',
      ordinal: 3,
      shortLabel: 'Infrastructure',
      question: 'What infrastructure must be built or confirmed?',
      recommendation: infrastructureRecommendation,
      status: statusFromSignals(infrastructureSignalCount, 4),
      bases: infrastructureSignalCount > 0
        ? uniqueBases([
            ...(infrastructureUsesSiteRecord ? ['site-fact' as const] : []),
            ...(structuredInfrastructureEvidence.length > 0 ? ['public-data' as const] : []),
            ...(infrastructureEvidence.length > 0 ? ['report-finding' as const] : []),
          ])
        : ['unavailable'],
      summary: infrastructureSummaryFacts.length > 0
        ? [
            `Known now: ${infrastructureSummaryFacts.slice(0, 5).join('; ')}.`,
            infrastructureEvidence[0]?.source.finding
              ? `The latest analysis also identifies ${infrastructureEvidence[0].source.finding}`
              : null,
            isIslandedArchitecture
              ? 'Still to confirm: carrier capacity and route diversity, on-site fuel supply, and the buildable layout.'
              : 'Still to confirm: firm utility service, interconnection upgrades, carrier capacity and route diversity, and the buildable layout.',
          ].filter((part): part is string => part != null).join(' ')
        : infrastructureEvidence[0]?.source.finding
          ? `One current analysis finding is available: ${infrastructureEvidence[0].source.finding} ${
              isIslandedArchitecture
                ? 'Carrier capacity and route diversity, on-site fuel supply, and the buildable layout remain unconfirmed; the declared plan has no utility grid connection.'
                : 'Utility service, interconnection upgrades, carrier capacity and route diversity, and the buildable layout remain unconfirmed.'
            }`
          : isIslandedArchitecture
            ? 'Carrier capacity and route diversity, on-site fuel supply, and the buildable layout are not yet confirmed; the declared plan has no utility grid connection.'
            : 'Utility service, interconnection scope, carrier capacity and route diversity, and the buildable layout are not yet confirmed.',
      details: [...structuredInfrastructureDetails, ...sourceDetails(infrastructureEvidence)],
      evidence: [
        ...(infrastructureUsesSiteRecord ? [siteSource] : []),
        ...structuredInfrastructureEvidence,
        ...infrastructureEvidence.map(({ source }) => source),
      ],
      nextInput: infrastructureSignalCount >= 4
        ? undefined
        : isIslandedArchitecture
          ? 'Confirm the unresolved carrier-route, on-site fuel supply, and buildable-layout items with the applicable provider, study, or design record.'
          : 'Confirm the unresolved utility/interconnection, carrier-route, and buildable-layout items with the applicable provider, study, or design record.',
    },
    {
      id: 'community-concerns',
      ordinal: 4,
      shortLabel: 'Community concerns',
      question: 'What noise, emissions, traffic, environmental, or visual concerns are presently identified?',
      recommendation: communityRecommendation,
      status: communityScreeningReady
        ? 'ready'
        : communityEvidenceTopicCount > 0 || communitySignalCount > 0
          ? 'partial'
          : 'unavailable',
      bases: communitySignalCount > 0
        ? uniqueBases([
            ...(promotedImpact ? ['modelled-screening' as const] : []),
            ...(communitySedEvidence.length > 0 ? ['public-data' as const] : []),
            ...(concernEvidence.length > 0 ? ['report-finding' as const] : []),
          ])
        : ['unavailable'],
      summary: [
        promotedImpact
          ? 'The promoted scenario contains screening results for residual emissions and receptor noise.'
          : null,
        communitySummaryFacts.length > 0
          ? `Current public screening identifies ${communitySummaryFacts.slice(0, 4).join('; ')}.`
          : hasStructuredCommunityContext
            ? `The exact-current SED supplies structured screening context for ${[...new Set(communityCoverageLabels)].join(', ')}.`
          : null,
        concernEvidence[0]?.source.finding
          ? `A current analysis adds: ${concernEvidence[0].source.finding}`
          : null,
        communityScreeningReady
          ? 'This supports an initial issue screen, not a conclusion that impacts are permitted, mitigated, or publicly accepted.'
          : null,
        communityEvidenceTopicCount <= 0 && communitySignalCount <= 0
          ? 'No current structured community-impact evidence is available. Noise, emissions, traffic, environmental, visual, and public-sentiment topics remain unresolved.'
          : communityUnresolvedTopics.length > 0
            ? `${communityUnresolvedLabel.charAt(0).toUpperCase()}${communityUnresolvedLabel.slice(1)} ${communityUnresolvedTopics.length === 1 ? 'remains' : 'remain'} unresolved rather than assumed clear.`
            : communitySignalCount > 0
            ? 'Any topic not specifically listed below remains unresolved rather than assumed clear.'
            : null,
      ].filter((part): part is string => part != null).join(' '),
      details: [...impactConcernDetails, ...communitySedDetails, ...sourceDetails(concernEvidence)],
      evidence: [
        ...(impactSource ? [impactSource] : []),
        ...communitySedEvidence,
        ...concernEvidence.map(({ source }) => source),
      ],
      ...(communityUnavailableCause ? { unavailableCause: communityUnavailableCause } : {}),
      nextInput: communityScreeningReady
        ? undefined
        : 'Add current noise/emissions, traffic/visual, environmental, and public-sentiment evidence for the topics still unresolved.',
    },
  ];

  if (options.economicContributionEnabled) {
    const economicOrdinal = questions.length + 1;
    const modelEvidence = (entryIds: readonly string[]): CallReadyEvidenceSource[] => {
      const bySourceId = new Map<string, CallReadyEvidenceSource>();
      for (const entryId of entryIds) {
        const entry = ECONOMIC_SOURCE_CATALOG.entries.find((candidate) => candidate.id === entryId);
        if (!entry) continue;
        for (const source of entry.sources) bySourceId.set(source.id, {
        id: `economic-source:${source.id}`,
        kind: 'model-result' as const,
        label: `${source.publisher} · ${source.title}`,
        vintage: source.publicationDate,
        url: source.url,
        excerpt: source.excerpt,
        locator: source.locator,
        evidenceRole: source.evidenceRole,
        });
      }
      return [...bySourceId.values()];
    };
    const unavailableEvidence: CallReadyEvidenceSource = {
      id: `economic-catalog:${ECONOMIC_SOURCE_CATALOG.catalogVersion}`,
      kind: 'model-result',
      label: `Economic source catalog · ${ECONOMIC_SOURCE_CATALOG.catalogVersion}`,
      vintage: ECONOMIC_SOURCE_CATALOG.createdAt,
    };
    const direct = economicSnapshot?.directJobs ?? null;
    const directRange = economicSnapshot?.directJobsMwRange ?? null;
    const rangeLowReady = directRange?.lowEndpoint.range != null;
    const rangeHighReady = directRange?.highEndpoint.range != null;
    const rangeReady = rangeLowReady && rangeHighReady;
    const rangePartiallyReady = rangeLowReady || rangeHighReady;
    const directReady = direct?.status === 'operator-provided'
      ? direct.range != null
      : rangeReady || direct?.range != null;
    const formatEconomicRange = (value: { low: number; high: number } | null): string => value
      ? value.low === value.high
        ? value.low.toLocaleString('en-US')
        : `${value.low.toLocaleString('en-US')}–${value.high.toLocaleString('en-US')}`
      : 'Not calculated';
    const formatWholePeopleRange = (value: { low: number; high: number } | null): string => value
      ? Math.floor(value.low) === Math.ceil(value.high)
        ? Math.floor(value.low).toLocaleString('en-US')
        : `${Math.floor(value.low).toLocaleString('en-US')}–${Math.ceil(value.high).toLocaleString('en-US')}`
      : 'not calculated';
    const endpointSummary = directRange
      ? [
          `${directRange.loadRange.low.toLocaleString('en-US')} MW: ${formatWholePeopleRange(directRange.lowEndpoint.range)} permanent direct onsite FTE`,
          `${directRange.loadRange.high.toLocaleString('en-US')} MW: ${formatWholePeopleRange(directRange.highEndpoint.range)} permanent direct onsite FTE`,
        ].join('; ')
      : null;
    const directStatus: CallReadyAnswerStatus = direct?.status === 'operator-provided' || rangeReady || (!directRange && directReady)
      ? 'ready'
      : rangePartiallyReady ? 'partial' : 'unavailable';
    const directVisualRows = (directRange ? [
      rangeVisualRow(
        'low-mw-permanent-jobs',
        `${directRange.loadRange.low.toLocaleString('en-US')} MW case`,
        directRange.lowEndpoint.range,
        `${formatWholePeopleRange(directRange.lowEndpoint.range)} FTE`,
      ),
      rangeVisualRow(
        'high-mw-permanent-jobs',
        `${directRange.loadRange.high.toLocaleString('en-US')} MW case`,
        directRange.highEndpoint.range,
        `${formatWholePeopleRange(directRange.highEndpoint.range)} FTE`,
      ),
    ] : [
      rangeVisualRow(
        'permanent-jobs',
        direct?.status === 'operator-provided' ? 'Operator staffing' : 'Screening estimate',
        direct?.range,
        `${formatWholePeopleRange(direct?.range ?? null)} FTE`,
      ),
    ]).filter((row): row is CallReadyRangeVisualRow => row != null);
    questions.push({
      id: 'permanent-jobs',
      ordinal: economicOrdinal,
      shortLabel: 'Permanent onsite jobs',
      question: 'How many permanent onsite jobs could this project support?',
      recommendation: directStatus === 'ready'
        ? direct?.status === 'operator-provided'
          ? recommend('proceed', 'Quote the operator-provided staffing figure and name it as such; keep the screening band as the independent cross-check.')
          : directRange
            ? recommend('proceed', 'Quote the two MW endpoint cases separately and never a midpoint; pair the figure with the county workforce profile before promising local hiring.')
            : recommend('proceed', 'Quote the screening band as a range with its MW basis; do not present a single number.')
        : directStatus === 'partial'
          ? recommend('input', 'Quote only the endpoint that passed the source policy; repair the other endpoint before presenting a range.')
          : recommend('input', 'Record a bounded project MW range, or an exact value with its basis, so the policy-valid jobs screen can run; do not substitute an industry ratio.'),
      status: directStatus,
      bases: uniqueBases([
        ...(directReady || rangePartiallyReady
          ? [direct?.evidenceBasis === 'operator-provided' ? 'operator-provided' as const : 'modelled-screening' as const]
          : ['unavailable' as const]),
        ...(copyApproved ? ['industry-context' as const] : []),
      ]),
      summary: direct?.status === 'operator-provided' && direct.range
        ? `The operator-provided value is ${formatEconomicRange(direct.range)} permanent direct onsite FTE for ${economicRequest!.directJobs.load.phaseBasis}.`
        : directRange && rangePartiallyReady
          ? `Using the recorded MW range, the policy-valid endpoint screen indicates ${endpointSummary}. These are separate endpoint results, not an interpolated estimate.`
          : directReady
            ? `The policy-valid screening method indicates ${formatEconomicRange(direct!.range)} permanent direct onsite FTE for ${economicRequest!.directJobs.load.phaseBasis}.`
        : economicRequest
          ? `Permanent jobs are not calculated: ${direct?.unavailableReason?.replaceAll('-', ' ') ?? 'the approved method is unavailable'}. The platform will not substitute an unreviewed ratio or silently convert the supplied MW basis.`
          : 'Permanent jobs are not calculated because neither a bounded project MW range nor an exact MW value is recorded.',
      details: [
        ...(directRange ? [
          { label: 'Recorded MW range', value: `${directRange.loadRange.low.toLocaleString('en-US')}–${directRange.loadRange.high.toLocaleString('en-US')} MW` },
          { label: `${directRange.loadRange.low.toLocaleString('en-US')} MW endpoint`, value: `${formatWholePeopleRange(directRange.lowEndpoint.range)} permanent direct onsite FTE` },
          { label: `${directRange.loadRange.high.toLocaleString('en-US')} MW endpoint`, value: `${formatWholePeopleRange(directRange.highEndpoint.range)} permanent direct onsite FTE` },
          { label: 'MW basis', value: staffingBasisLabel(directRange.loadRange) },
          { label: 'Phase basis', value: directRange.loadRange.phaseBasis },
        ] : economicRequest ? [
          { label: 'Load used', value: `${economicRequest.directJobs.load.value.toLocaleString('en-US')} MW` },
          { label: 'MW basis', value: staffingBasisLabel(economicRequest.directJobs.load) },
          { label: 'Phase basis', value: economicRequest.directJobs.load.phaseBasis },
          { label: 'Operating model', value: economicRequest.directJobs.operatingModel },
          { label: 'Workload', value: economicRequest.directJobs.workload },
        ] : []),
        ...(copyApproved ? [{
          label: 'Why staffing per MW varies',
          value: CALL_READY_INDUSTRY_CONTEXT.staffingVariation,
        }, {
          label: 'Context boundary',
          value: 'Industry context explains the range; it is not a site staffing commitment and does not replace an operator staffing plan.',
        }] : []),
      ],
      evidence: [
        ...(directRange && directRange.sourceEntryIds.length > 0
          ? modelEvidence(directRange.sourceEntryIds)
          : direct && direct.sourceEntryIds.length > 0
            ? modelEvidence(direct.sourceEntryIds)
            : direct?.evidenceBasis === 'operator-provided' ? [{
                id: `operator-direct-jobs:${economicSnapshot?.snapshotId}`,
                kind: 'operator-input' as const,
                label: 'Operator-provided permanent onsite staffing input',
                vintage: economicSnapshot?.generatedAt,
              }] : [unavailableEvidence]),
        ...(copyApproved ? [copySource] : []),
      ],
      visuals: directVisualRows.length > 0 ? [{
        id: 'permanent-jobs-envelope',
        title: 'Permanent onsite workforce',
        unitLabel: 'FTE',
        rows: directVisualRows,
        note: directRange
          ? 'Independent endpoint screens; the space between endpoints is not an interpolated forecast.'
          : 'Direct onsite roles only; indirect and induced employment is excluded.',
      }] : undefined,
      nextInput: !economicRequest
        ? 'Record a bounded project MW range or an exact MW value and its basis.'
        : directRange?.lowEndpoint.unavailableReason === 'unsupported-mw-basis'
          || directRange?.highEndpoint.unavailableReason === 'unsupported-mw-basis'
          || direct?.unavailableReason === 'unsupported-mw-basis'
          ? 'Confirm total utility-service MW; IT and facility MW are retained but not silently converted.'
          : rangeReady || (!directRange && directReady)
            ? undefined
            : 'Repair the source-policy validation failure or add the missing independent source evidence.',
    });

    const construction = economicSnapshot?.constructionJobs ?? null;
    const constructionRange = economicSnapshot?.constructionJobsMwRange ?? null;
    const constructionPointReady = construction?.peakOnsiteWorkers != null
      || construction?.averageOnsiteWorkers != null
      || construction?.directJobYears != null;
    const constructionEndpointReady = (result: typeof construction): boolean => result != null && (
      result.peakOnsiteWorkers != null || result.averageOnsiteWorkers != null || result.directJobYears != null
    );
    const constructionLowReady = constructionEndpointReady(constructionRange?.lowEndpoint ?? null);
    const constructionHighReady = constructionEndpointReady(constructionRange?.highEndpoint ?? null);
    const constructionRangeReady = constructionLowReady && constructionHighReady;
    const constructionRangePartiallyReady = constructionLowReady || constructionHighReady;
    const constructionReady = construction?.status === 'operator-provided'
      ? constructionPointReady
      : constructionRangeReady || (!constructionRange && constructionPointReady);
    const formatDuration = (value: typeof construction extends null ? never : NonNullable<typeof construction>['durationMonths']): string => value == null
      ? 'duration not calculated'
      : typeof value === 'number'
        ? `${value.toLocaleString('en-US')} months`
        : `${value.low.toLocaleString('en-US')}–${value.high.toLocaleString('en-US')} months`;
    const constructionPeakRanges = constructionRange
      ? [
          constructionRange.lowEndpoint.peakOnsiteWorkers,
          constructionRange.highEndpoint.peakOnsiteWorkers,
        ].filter((range): range is { low: number; high: number } => range != null)
      : [];
    const constructionPeakEnvelope = constructionPeakRanges.length > 0 ? {
      low: Math.min(...constructionPeakRanges.map(range => range.low)),
      high: Math.max(...constructionPeakRanges.map(range => range.high)),
    } : null;
    const constructionStatus: CallReadyAnswerStatus = construction?.status === 'operator-provided'
      || constructionRangeReady || (!constructionRange && constructionPointReady)
      ? 'ready'
      : constructionRangePartiallyReady ? 'partial' : 'unavailable';
    const constructionVisualRows = (constructionRange ? [
      rangeVisualRow(
        'low-mw-peak-workers',
        `${constructionRange.loadRange.low.toLocaleString('en-US')} MW · peak`,
        constructionRange.lowEndpoint.peakOnsiteWorkers,
        `${formatWholePeopleRange(constructionRange.lowEndpoint.peakOnsiteWorkers)} people`,
      ),
      rangeVisualRow(
        'low-mw-average-workers',
        `${constructionRange.loadRange.low.toLocaleString('en-US')} MW · average`,
        constructionRange.lowEndpoint.averageOnsiteWorkers,
        `${formatWholePeopleRange(constructionRange.lowEndpoint.averageOnsiteWorkers)} people`,
      ),
      rangeVisualRow(
        'high-mw-peak-workers',
        `${constructionRange.loadRange.high.toLocaleString('en-US')} MW · peak`,
        constructionRange.highEndpoint.peakOnsiteWorkers,
        `${formatWholePeopleRange(constructionRange.highEndpoint.peakOnsiteWorkers)} people`,
      ),
      rangeVisualRow(
        'high-mw-average-workers',
        `${constructionRange.loadRange.high.toLocaleString('en-US')} MW · average`,
        constructionRange.highEndpoint.averageOnsiteWorkers,
        `${formatWholePeopleRange(constructionRange.highEndpoint.averageOnsiteWorkers)} people`,
      ),
    ] : [
      rangeVisualRow(
        'peak-workers',
        'Peak onsite',
        construction?.peakOnsiteWorkers,
        `${formatWholePeopleRange(construction?.peakOnsiteWorkers ?? null)} people`,
      ),
      rangeVisualRow(
        'average-workers',
        'Average onsite',
        construction?.averageOnsiteWorkers,
        `${formatWholePeopleRange(construction?.averageOnsiteWorkers ?? null)} people`,
      ),
    ]).filter((row): row is CallReadyRangeVisualRow => row != null);
    questions.push({
      id: 'construction-jobs',
      ordinal: economicOrdinal + 1,
      shortLabel: 'Construction activity',
      question: 'How many construction workers could be onsite, and for how long?',
      recommendation: constructionStatus === 'ready'
        ? recommend('proceed', 'Present peak and average onsite workers as separate figures with the duration; do not add an indirect-job multiplier.')
        : constructionStatus === 'partial'
          ? recommend('input', 'Present only the endpoint inside the policy-valid research domain; add a supported source for the other project scale before quoting a range.')
          : recommend('input', 'Do not quote construction employment yet; provide an operator or EPC staffing plan, or a supported method for this project scale.'),
      status: constructionStatus,
      bases: constructionReady || constructionRangePartiallyReady
        ? [construction?.evidenceBasis === 'operator-provided' ? 'operator-provided' : 'modelled-screening']
        : ['unavailable'],
      summary: construction?.status === 'operator-provided' && constructionPointReady
        ? `The operator/EPC-provided construction values are ${formatEconomicRange(construction.peakOnsiteWorkers)} peak onsite workers, ${formatEconomicRange(construction.averageOnsiteWorkers)} average onsite workers, ${formatDuration(construction.durationMonths)}, and ${formatEconomicRange(construction.directJobYears)} direct job-years.`
        : constructionRange && constructionRangePartiallyReady
          ? `Across the ${constructionRange.loadRange.low.toLocaleString('en-US')}–${constructionRange.loadRange.high.toLocaleString('en-US')} MW endpoint cases, the research-backed screen estimates ${formatWholePeopleRange(constructionPeakEnvelope)} peak onsite workers. The chart separates peak and average workforce; duration and direct job-years remain in Key facts. No indirect-job multiplier is applied.`
          : constructionReady
            ? `The research-backed screen estimates ${formatEconomicRange(construction!.peakOnsiteWorkers)} peak onsite workers, ${formatEconomicRange(construction!.averageOnsiteWorkers)} average onsite workers, ${formatDuration(construction!.durationMonths)}, and ${formatEconomicRange(construction!.directJobYears)} direct job-years.`
        : 'No policy-valid construction estimate is available. Peak workers, average workers, and job-years remain separate and no indirect-job multiplier is applied.',
      details: constructionRange ? [
        { label: 'Recorded MW range', value: `${constructionRange.loadRange.low.toLocaleString('en-US')}–${constructionRange.loadRange.high.toLocaleString('en-US')} MW` },
        {
          label: `${constructionRange.loadRange.low.toLocaleString('en-US')} MW endpoint`,
          value: `${formatWholePeopleRange(constructionRange.lowEndpoint.peakOnsiteWorkers)} peak · ${formatWholePeopleRange(constructionRange.lowEndpoint.averageOnsiteWorkers)} average · ${formatDuration(constructionRange.lowEndpoint.durationMonths)} · ${formatWholePeopleRange(constructionRange.lowEndpoint.directJobYears)} job-years`,
        },
        {
          label: `${constructionRange.loadRange.high.toLocaleString('en-US')} MW endpoint`,
          value: `${formatWholePeopleRange(constructionRange.highEndpoint.peakOnsiteWorkers)} peak · ${formatWholePeopleRange(constructionRange.highEndpoint.averageOnsiteWorkers)} average · ${formatDuration(constructionRange.highEndpoint.durationMonths)} · ${formatWholePeopleRange(constructionRange.highEndpoint.directJobYears)} job-years`,
        },
        { label: 'MW basis', value: staffingBasisLabel(constructionRange.loadRange) },
      ] : construction ? [
        ...(economicRequest ? [
          { label: 'Load used', value: `${economicRequest.constructionJobs!.load.value.toLocaleString('en-US')} MW` },
          { label: 'MW basis', value: staffingBasisLabel(economicRequest.constructionJobs!.load) },
        ] : []),
        { label: 'Peak onsite workers', value: formatEconomicRange(construction.peakOnsiteWorkers) },
        { label: 'Average onsite workers', value: formatEconomicRange(construction.averageOnsiteWorkers) },
        { label: 'Direct job-years', value: formatEconomicRange(construction.directJobYears) },
        {
          label: 'Duration',
          value: construction.durationMonths == null
            ? 'Not calculated'
            : typeof construction.durationMonths === 'number'
              ? `${construction.durationMonths} months`
              : `${construction.durationMonths.low}–${construction.durationMonths.high} months`,
        },
        { label: 'Phase basis', value: construction.phaseBasis },
      ] : [],
      evidence: constructionRange && constructionRange.sourceEntryIds.length > 0
        ? modelEvidence(constructionRange.sourceEntryIds)
        : construction && construction.sourceEntryIds.length > 0
          ? modelEvidence(construction.sourceEntryIds)
        : construction?.evidenceBasis === 'operator-provided' ? [{
            id: `operator-construction-jobs:${economicSnapshot?.snapshotId}`,
            kind: 'operator-input',
            label: 'Operator/EPC-provided construction staffing input',
            vintage: economicSnapshot?.generatedAt,
          }] : [unavailableEvidence],
      visuals: constructionVisualRows.length > 0 ? [{
        id: 'construction-workforce-envelope',
        title: 'Onsite construction workforce',
        unitLabel: 'people',
        rows: constructionVisualRows,
        note: 'Peak and average workers are separate direct-onsite measures; job-years and indirect employment are not combined here.',
      }] : undefined,
      nextInput: constructionRangeReady || constructionReady
        ? undefined
        : constructionRangePartiallyReady
          ? 'One endpoint is outside the policy-valid research domain; add a supported source for that project scale.'
          : 'Provide an operator/EPC staffing plan or add a supported construction method for this project scale.',
    });

    const profile = economicProfile;
    const occupationWageObservations = profile?.observations.filter((observation) => (
      observation.metric === 'median-hourly-wage' && observation.occupation != null
    )) ?? [];
    const occupationEmploymentObservations = profile?.observations.filter((observation) => (
      observation.metric === 'employment' && observation.occupation != null
    )) ?? [];
    const laborMarketObservations = profile?.observations.filter((observation) => (
      observation.metric === 'labor-force' || observation.metric === 'unemployment-rate'
    )) ?? [];
    const workforceObservations = [
      ...occupationEmploymentObservations,
      ...occupationWageObservations,
      ...laborMarketObservations,
    ];
    const profileEvidence = (observations: readonly EconomicMetricObservation[]): CallReadyEvidenceSource[] => {
      const sources = new Map<string, CallReadyEvidenceSource>();
      for (const observation of observations) {
        const subject = observation.occupation?.code ?? observation.industry?.code ?? 'all';
        const id = `economic-profile:${observation.sourceDataset}:${observation.geography.code}:${subject}:${observation.period}`;
        if (sources.has(id)) continue;
        sources.set(id, {
          id,
          kind: 'public-dataset',
          label: `${observation.sourceDataset} · ${observation.geography.label}${observation.industry ? ` · ${observation.industry.label}` : ''}${observation.occupation ? ` · ${observation.occupation.label}` : ''}`,
          vintage: observation.period,
          url: observation.sourceUrl,
        });
      }
      return [...sources.values()];
    };
    const workforcePresentation = profile ? buildWorkforceProfilePresentation(profile) : null;
    const profileReady = workforcePresentation?.status === 'available';
    const profileHasWorkforceData = workforcePresentation != null && workforcePresentation.status !== 'unavailable';
    questions.push({
      id: 'workforce-wages',
      ordinal: economicOrdinal + 2,
      shortLabel: 'Workforce and wages',
      question: 'Are these good-paying jobs, and can they be hired locally?',
      recommendation: profileReady
        ? recommend('proceed', 'Use the published county wages and labor-force figures as the local-hiring basis; commit to project wages only through an operator hiring plan.')
        : profileHasWorkforceData
          ? recommend('input', 'Cite only the observations that loaded; refresh the county profile to fill the wage or labor-force gaps before making a local-hiring claim.')
          : recommend('input', 'Make no local-availability or wage claim until the county workforce profile loads.'),
      status: profileReady ? 'ready' : profileHasWorkforceData ? 'partial' : 'unavailable',
      bases: uniqueBases([
        profileHasWorkforceData ? 'public-data' : 'unavailable',
        ...(copyApproved ? ['industry-context' as const] : []),
      ]),
      summary: workforcePresentation
        ? buildWorkforceProfileAnswer(workforcePresentation)
        : 'No county workforce profile is attached to the exact current record, so the platform makes no local-availability or wage claim. Resolve or refresh the site-to-county match to load the published labor-market evidence.',
      details: [
        ...(copyApproved ? [{
          label: 'Typical operating roles',
          value: CALL_READY_INDUSTRY_CONTEXT.operatingWorkforce,
        }, {
          label: 'Context boundary',
          value: 'These role families are industry context only; local availability and project wages require the published area profile and an operator hiring plan.',
        }] : []),
        ...workforceObservations.slice(0, 14).map((observation) => ({
        label: `${observation.metric.replaceAll('-', ' ')} · ${observation.occupation?.label ?? observation.industry?.label ?? observation.geography.label}`,
        value: `${observation.value.toLocaleString('en-US')} ${observation.units} · ${observation.period}`,
        })),
        ...(profile?.providerErrors.slice(0, 3).map((error) => ({ label: 'Provider status', value: error })) ?? []),
      ],
      evidence: [
        ...profileEvidence(workforceObservations),
        ...(copyApproved ? [copySource] : []),
      ],
      nextInput: profileHasWorkforceData
        ? undefined
        : 'Resolve or refresh the U.S. county match, then load the federal workforce profile; no manual wage estimate is required.',
    });

    const peers = economicSnapshot?.comparableMatches ?? [];
    questions.push({
      id: 'comparables',
      ordinal: economicOrdinal + 3,
      shortLabel: 'Comparable projects',
      question: 'What happened at comparable US data center projects?',
      recommendation: peers.length > 0
        ? recommend('proceed', `Cite the ${peers.length} policy-valid ${peers.length === 1 ? 'project' : 'projects'} individually; present a range only when at least three compatible records share an evidence basis.`)
        : recommend('input', 'Do not cite peer projects yet; load at least one policy-valid public-record observation first.'),
      status: peers.length > 0 ? 'ready' : 'unavailable',
      bases: peers.length > 0 ? ['public-record'] : ['unavailable'],
      summary: peers.length > 0
        ? `${peers.length} policy-valid active ${peers.length === 1 ? 'project is' : 'projects are'} available. Individual observations are visible immediately; statistical ranges appear only with at least three compatible records of the same evidence basis.`
        : 'No policy-valid compatible project record is available. The base jobs screen remains independent of comparable coverage.',
      details: peers.slice(0, 5).map((match) => ({
        label: match.record.facilityName,
        value: `${match.record.permanentJobs?.value.toLocaleString('en-US') ?? (match.record.permanentJobsRange ? `${match.record.permanentJobsRange.value.low.toLocaleString('en-US')}–${match.record.permanentJobsRange.value.high.toLocaleString('en-US')}` : 'Unknown')} permanent jobs · ${match.record.permanentJobs?.evidenceBasis ?? match.record.permanentJobsRange?.evidenceBasis ?? 'no jobs observation'} · match score ${match.score}`,
      })),
      evidence: peers.flatMap((match) => (match.record.permanentJobs?.sources ?? match.record.permanentJobsRange?.sources)?.map((source) => ({
        id: `comparable:${match.record.projectId}:${source.id}`,
        kind: 'public-record' as const,
        label: `${source.publisher} · ${source.title}`,
        vintage: source.publicationDate,
        url: source.url,
      })) ?? []),
      nextInput: peers.length > 0 ? undefined : 'Load at least one compatible US public-record observation that passes the versioned record policy.',
    });

    const fiscal = economicSnapshot?.fiscalScenarios ?? [];
    questions.push({
      id: 'fiscal',
      ordinal: economicOrdinal + 4,
      shortLabel: 'Taxes and community revenue',
      question: 'What property-tax or PILOT revenue could the community receive?',
      recommendation: fiscal.length > 0
        ? recommend('caution', 'Present the scenarios as illustrative arithmetic; obtain the jurisdiction\'s assessment method and any PILOT terms in writing before quoting revenue externally.')
        : recommend('input', 'Create one illustrative scenario with the assessed value, effective tax rate and term before discussing community revenue.'),
      status: fiscal.length > 0 ? 'ready' : 'unavailable',
      bases: fiscal.length > 0 ? ['modelled-screening'] : ['unavailable'],
      summary: fiscal.length > 0
        ? `${fiscal.length} user-defined illustrative fiscal ${fiscal.length === 1 ? 'scenario is' : 'scenarios are'} available. These are arithmetic screens, not eligibility or legal conclusions.`
        : 'No property-tax or PILOT scenario has been created. An illustrative calculation needs a taxable assessed value and combined local property-tax rate; abatement and PILOT terms are optional.',
      details: fiscal.map((scenario) => ({
        label: scenario.jurisdictionLabel,
        value: `$${scenario.cumulativeCommunityRevenueUsd.toLocaleString('en-US')} cumulative nominal community revenue · ${scenario.years.length} years · ${scenario.currencyYear} USD`,
      })),
      evidence: fiscal.length > 0 ? [{
        id: `fiscal-scenarios:${economicSnapshot?.snapshotId}`,
        kind: 'model-result',
        label: 'User-defined manual fiscal scenario inputs',
        vintage: economicSnapshot?.generatedAt,
      }] : [],
      nextInput: fiscal.length > 0
        ? undefined
        : 'Create one illustrative scenario with the jurisdiction, assessed value, effective annual tax rate, term, and any known abatement or PILOT amount.',
    });
  }

  // Power & cooling and its risks lead; infrastructure and community
  // concerns follow; the economic questions keep their governance after them.
  const presentationOrder: readonly CallReadyQuestionId[] = [
    'resources',
    'power-cooling-risks',
    'infrastructure',
    'community-concerns',
    'permanent-jobs',
    'construction-jobs',
    'workforce-wages',
    'comparables',
    'fiscal',
  ];
  const enrichedQuestions = enrichStageAnswers({ site, sed: currentSed, stage, promoted: promotedImpact ?? null, questions, usableReport, screeningAssessment, trusted: trustedSedSection });
  const governedQuestions = enrichedQuestions.filter(question => question.id !== 'fiscal' || stage.briefStage === 'mature-validation' || (economicSnapshot?.fiscalScenarios.length ?? 0) > 0)
    .sort((left, right) => {
      const leftIndex = presentationOrder.indexOf(left.id);
      const rightIndex = presentationOrder.indexOf(right.id);
      return (leftIndex < 0 ? presentationOrder.length : leftIndex)
        - (rightIndex < 0 ? presentationOrder.length : rightIndex);
    })
    .map((question, index) => {
      const presented = { ...question, ordinal: index + 1 };
      return {
        ...presented,
        ...(copyApproved ? { spokenSummary: renderCallReadySpokenSummary(presented) } : {}),
      };
    });
  const recordState = options.analysesState ?? 'settled';
  const presentedQuestions = recordState === 'loading' ? [] : governedQuestions;
  const siteQuestions = presentedQuestions.filter((question) => !REFERENCE_QUESTION_IDS.has(question.id));

  return {
    stage,
    promptQuestions: recordState === 'loading' ? [] : questions,
    siteId: site.id,
    siteName: site.name,
    generatedFrom: 'existing-site-data-and-findings',
    copyGovernance: CALL_READY_COPY_GOVERNANCE,
    copyApproved,
    recordState,
    questions: presentedQuestions,
    readyCount: siteQuestions.filter((question) => question.status === 'ready').length,
    siteQuestionCount: siteQuestions.length,
    referenceCount: presentedQuestions.filter((question) => question.status === 'reference').length,
    evidenceSourceCount: new Set(
      presentedQuestions.flatMap((question) => question.evidence).map((source) => source.id),
    ).size,
    economicContributionEnabled: options.economicContributionEnabled === true,
    economicSnapshot,
    economicProfile,
    economicSnapshotAuthority: economicSnapshot == null
      ? null
      : economicSnapshot === storedEconomicSnapshot
        ? 'server-authored'
        : 'local-unavailable-preview',
    economicInputs: site.economicContributionInputs ?? {},
    economicScreeningDefaults,
    economicDefaultLoadMw: economicLoadMw ?? (economicLoadRange == null ? power.mw : null),
    economicDefaultLoadRange: economicLoadRange != null
      ? { low: economicLoadRange.min, high: economicLoadRange.max }
      : economicLoadMw == null
        && !power.isExact
        && recordedPowerRange != null
        && Number.isFinite(recordedPowerRange.min)
        && Number.isFinite(recordedPowerRange.max)
        && recordedPowerRange.min > 0
          ? { low: recordedPowerRange.min, high: recordedPowerRange.max }
          : null,
    economicDefaultLoadBasis: site.economicContributionInputs?.loadBasis
      ?? (site.projectIntent?.powerBasis === 'it-load' ? 'it' : 'facility'),
  };
}
